// ============================================
// HOMELY - Language Context
// ============================================
// Provides internationalization (i18n) support
// Languages: English, Hindi, Kannada
// Last updated: 2026-02-03 - Fixed AI translations
// ============================================

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// --------------------------------------------
// TRANSLATIONS
// --------------------------------------------

const translations = {
    en: {
        // Common
        appName: 'Homely',
        tagline: 'Natural Remedies for Modern Life',
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        submit: 'Submit',
        search: 'Search',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        yes: 'Yes',
        no: 'No',
        or: 'or',
        and: 'and',
        
        // Navigation
        nav: {
            home: 'Home',
            dashboard: 'Dashboard',
            aiAssistant: 'AI Assistant',
            createRemedy: 'Create Remedy',
            profile: 'Profile',
            settings: 'Settings',
            login: 'Login',
            signup: 'Sign Up',
            logout: 'Log out',
            please: 'Please',
            or: 'or',
        },
        
        // Home Page
        home: {
            heroTitle: 'Discover Natural',
            heroTitleHighlight: 'Home Remedies',
            heroSubtitle: 'Explore time-tested natural solutions for everyday health concerns. Your wellness journey starts here.',
            exploreRemedies: 'Explore Remedies',
            askAI: 'Ask AI Assistant',
            searchRemedies: 'Search Remedies',
            featuresTitle: 'Why Choose Homely?',
            feature1Title: 'Natural Solutions',
            feature1Desc: 'Discover remedies using natural ingredients found in your kitchen',
            feature2Title: 'AI-Powered Help',
            feature2Desc: 'Get personalized remedy suggestions from our smart AI assistant',
            feature3Title: 'Community Driven',
            feature3Desc: 'Share and learn from a community of natural health enthusiasts',
            feature4Title: 'Safe & Verified',
            feature4Desc: 'All remedies are reviewed for safety and effectiveness',
            forEverydayWellness: 'for Everyday Wellness',
            badge: 'Traditional Wisdom, Modern Wellness',
            naturalRemedies: 'Natural Remedies',
            categories: 'Categories',
            happyUsers: 'Happy Users',
            timeTested: 'Time-Tested Remedies',
            timeTestedDesc: 'Access centuries-old recipes verified by generations of traditional healers and Ayurvedic practitioners.',
            simpleIngredients: 'Simple Ingredients',
            simpleIngredientsDesc: 'Create powerful natural remedies using everyday kitchen ingredients like turmeric, ginger, and honey.',
            safeNatural: 'Safe & Natural',
            safeNaturalDesc: 'Gentle remedies without harmful chemicals. Each remedy includes safety guidelines and precautions.',
            aiAssistant: 'AI Health Assistant',
            aiAssistantDesc: 'Get personalized guidance from our intelligent assistant trained on traditional medicine knowledge.',
            detailedInstructions: 'Detailed Instructions',
            detailedInstructionsDesc: 'Step-by-step preparation guides with ingredient lists, timing, and dosage recommendations.',
            communityWisdom: 'Community Wisdom',
            communityWisdomDesc: 'Join thousands sharing their experiences and traditional knowledge.',
            howItWorks: 'How It Works',
            step1Title: 'Browse Remedies',
            step1Desc: 'Explore our curated collection of natural remedies for common ailments.',
            step2Title: 'Get Ingredients',
            step2Desc: 'Most ingredients are already in your kitchen or easily available.',
            step3Title: 'Follow Instructions',
            step3Desc: 'Simple step-by-step guides make preparation easy.',
            step4Title: 'Feel Better',
            step4Desc: 'Experience the healing power of natural remedies.',
            testimonials: 'What Our Users Say',
            testimonialsSubtitle: 'Join thousands who have discovered the power of natural remedies',
            communityStories: 'Community Stories',
            whatCommunitySays: 'What Our Community Says',
            realExperiences: 'Real experiences from real people using natural remedies',
            beFirstToShare: 'Be the First to Share Your Experience!',
            communityGettingStarted: 'Our community is just getting started. Share how natural remedies have helped you and inspire others on their wellness journey.',
            writeTestimonial: 'Write a Testimonial',
            loginToShare: 'Login to Share Your Story',
            shareYourExperience: 'Share Your Experience',
            updateYourTestimonial: 'Update Your Testimonial',
            yourRating: 'Your Rating',
            yourTestimonial: 'Your Testimonial',
            testimonialPlaceholder: 'Share how Homely has helped you on your wellness journey...',
            characters: 'characters',
            submitting: 'Submitting...',
            submitTestimonial: 'Submit Testimonial',
            updateTestimonial: 'Update Testimonial',
            testimonialUpdated: 'Testimonial updated successfully!',
            thankYouTestimonial: 'Thank you for your testimonial!',
            editMyTestimonial: 'Edit My Testimonial',
            shareYourStory: 'Share Your Story',
            ctaTitle: 'Start Your Natural Wellness Journey',
            ctaSubtitle: 'Join thousands of users who trust Homely for their health needs.',
            getStartedFree: 'Get Started Free',
            learnMore: 'Learn More',
            popularCategories: 'Popular Categories',
            viewAll: 'View All',
            readyToStart: 'Ready to Start?',
            joinCommunity: 'Join our community of natural wellness enthusiasts',
            signUpFree: 'Sign Up Free',
            freeForever: 'Free Forever',
            safe: '100% Safe',
            noAds: 'No Ads',
            alreadyMember: 'Already a member?',
            signIn: 'Sign In',
        },
        
        // Dashboard
        dashboard: {
            title: 'Discover Natural Remedies',
            subtitle: 'Browse our collection of time-tested home remedies for various health concerns',
            searchPlaceholder: 'Search remedies by name, ingredient, or condition...',
            voiceSearch: 'Voice search',
            addRemedy: 'Add Remedy',
            noResults: 'No remedies found',
            noResultsDesc: 'Try adjusting your search or filters',
            clearFilters: 'Clear Filters',
            sortBy: 'Sort by',
            newest: 'Newest First',
            oldest: 'Oldest First',
            popular: 'Most Popular',
            highestRated: 'Highest Rated',
            alphabetical: 'A-Z',
            remediesFound: 'remedies found',
            loadMore: 'Load More Remedies',
            showing: 'Showing',
            of: 'of',
            guestBanner: "You're viewing sample remedies. Sign in to unlock all features!",
            signInNow: 'Sign In Now',
            loginRequired: 'Login Required',
            loginRequiredDesc: 'Sign in to view full remedy details, save favorites, and access all features!',
            loginToSearch: 'Please login to search remedies',
            loginToFilter: 'Please login to filter by category',
            loginToSort: 'Please login to sort remedies',
            loginToSave: 'Please login to save remedies',
            remedySaved: 'Remedy saved!',
            maybeLater: 'Maybe Later',
        },
        
        // Categories
        categories: {
            all: 'All',
            immunityBooster: 'Immunity',
            digestiveHealth: 'Digestive',
            respiratoryHealth: 'Respiratory',
            skinCare: 'Skin Care',
            hairCare: 'Hair Care',
            painRelief: 'Pain Relief',
            stressRelief: 'Stress Relief',
            sleepAid: 'Sleep Aid',
            energyBoost: 'Energy',
            detox: 'Detox',
            weightManagement: 'Weight',
            heartHealth: 'Heart Health',
            boneJointHealth: 'Bone & Joint',
            eyeHealth: 'Eye Health',
            oralHealth: 'Oral Health',
            womensHealth: "Women's Health",
            mensHealth: "Men's Health",
            childHealth: 'Child Health',
            elderCare: 'Elder Care',
            other: 'Other',
            // Categories Page
            title: 'Browse Categories',
            subtitle: 'Explore our collection of natural remedies organized by health concern.',
            immunityBoosterDesc: 'Strengthen your immune system with natural remedies.',
            digestiveHealthDesc: 'Remedies for better digestion and gut health.',
            coldAndCough: 'Cold & Cough',
            coldAndCoughDesc: 'Natural relief for cold, cough, and flu symptoms.',
            skinCareDesc: 'Natural solutions for healthy, glowing skin.',
            hairCareDesc: 'Traditional remedies for strong, healthy hair.',
            stressReliefDesc: 'Natural ways to reduce stress and anxiety.',
            energyVitality: 'Energy & Vitality',
            energyVitalityDesc: 'Boost your energy levels naturally.',
            generalWellness: 'General Wellness',
            generalWellnessDesc: 'Overall health and well-being remedies.',
            explore: 'Explore',
            backToCategories: 'Back to Categories',
            remediesFound: 'remedies found',
            loginRequired: 'Login Required',
            loginToView: 'Please log in to view remedies in this category.',
            noRemedies: 'No Remedies Yet',
            noRemediesDesc: 'Be the first to add a remedy in this category!',
            createFirst: 'Create First Remedy',
        },
        
        // Remedy Detail
        remedy: {
            ingredients: 'Ingredients',
            instructions: 'Instructions',
            reviews: 'Reviews',
            preparation: 'Preparation',
            benefits: 'Benefits',
            precautions: 'Precautions',
            warnings: 'Warnings',
            dosage: 'Dosage',
            bestTime: 'Best Time to Use',
            difficulty: 'Difficulty',
            prepTime: 'Prep Time',
            servings: 'Servings',
            minutes: 'minutes',
            easy: 'Easy',
            medium: 'Medium',
            hard: 'Hard',
            shareRemedy: 'Share',
            saveRemedy: 'Save Remedy',
            print: 'Print',
            like: 'Like',
            liked: 'Liked',
            reportRemedy: 'Report',
            createdBy: 'Created by',
            sharedBy: 'Shared by',
            communityMember: 'Community Member',
            askAboutRemedy: 'Ask AI about this remedy',
            notFound: 'Remedy Not Found',
            notFoundDesc: "The remedy you're looking for doesn't exist.",
            backToDashboard: 'Back to Dashboard',
            loadError: 'Failed to load remedy. Please try again.',
            savedToCollection: 'Saved to collection!',
            removedFromSaved: 'Removed from saved',
            saveError: 'Failed to save remedy',
            loginToLike: 'Please login to like remedies',
            loginToRate: 'Please login to rate remedies',
            thanksForRating: 'Thanks for rating!',
            ratingError: 'Failed to submit rating',
            linkCopied: 'Link copied to clipboard!',
            rateThisRemedy: 'Rate This Remedy',
            youRated: 'You rated',
            stars: 'stars',
            clickToRate: 'Click to rate',
            noReviews: 'No reviews yet.',
            beFirstToReview: 'Be the first to share your experience!',
            anonymous: 'Anonymous',
        },
        
        // Create Remedy
        create: {
            title: 'Create New Remedy',
            editTitle: 'Edit Remedy',
            basicInfo: 'Basic Information',
            remedyTitle: 'Title',
            titlePlaceholder: 'e.g., Ginger Honey Tea for Cold',
            description: 'Description',
            descPlaceholder: 'Brief description of the remedy...',
            category: 'Category',
            selectCategory: 'Select a category',
            addIngredient: 'Add Ingredient',
            addStep: 'Add Step',
            ingredientName: 'Ingredient',
            quantity: 'Quantity',
            unit: 'Unit',
            optional: 'Optional',
            stepInstruction: 'Instruction',
            duration: 'Duration',
            tips: 'Tips',
            publishRemedy: 'Publish Remedy',
            saveDraft: 'Save Draft',
            aiAssist: 'AI Assist',
        },
        
        // Create Remedy Page (detailed)
        createRemedy: {
            title: 'Create New Remedy',
            subtitle: 'Share your natural remedy with the community',
            basicInfo: 'Basic Information',
            name: 'Remedy Name',
            namePlaceholder: 'e.g., Honey Ginger Tea for Cold',
            description: 'Description',
            descriptionPlaceholder: 'Describe your remedy and its benefits...',
            category: 'Category',
            difficulty: 'Difficulty Level',
            preparationTime: 'Preparation Time (minutes)',
            optional: 'Optional',
            ingredients: 'Ingredients',
            addIngredient: 'Add Ingredient',
            ingredientName: 'Ingredient name',
            amount: 'Amount',
            unit: 'Unit',
            steps: 'Instructions',
            stepInstruction: 'Add step-by-step instructions',
            addStep: 'Add Step',
            stepPlaceholder: 'Describe this step...',
            tips: 'Additional Information',
            benefits: 'Benefits',
            addBenefit: 'Add Benefit',
            benefitPlaceholder: 'e.g., Boosts immunity',
            warnings: 'Warnings & Precautions',
            addWarning: 'Add Warning',
            warningPlaceholder: 'e.g., Not recommended during pregnancy',
            tags: 'Tags',
            addTag: 'Add Tag',
            tagPlaceholder: 'e.g., immunity, cold, natural',
            imageUrl: 'Image URL',
            publishRemedy: 'Publish Remedy',
            publishing: 'Publishing...',
            saveDraft: 'Save Draft',
            next: 'Next',
            previous: 'Previous',
            step: 'Step',
            of: 'of',
            reviewSubmit: 'Review & Submit',
            reviewInfo: 'Review your remedy before publishing',
            remedySummary: 'Remedy Summary',
            summaryTitle: 'Title',
            items: 'items',
            stepsCount: 'steps',
            success: 'Remedy created successfully!',
            error: 'Failed to create remedy',
            // Categories for create form
            categoryDigestive: 'Digestive Health',
            categoryRespiratory: 'Respiratory',
            categorySkin: 'Skin Care',
            categoryImmunity: 'Immunity',
            categorySleep: 'Sleep & Relaxation',
            categoryPain: 'Pain Relief',
            categoryHair: 'Hair Care',
            categoryEnergy: 'Energy & Vitality',
            categoryStress: 'Stress Relief',
            categoryOther: 'Other',
            // Difficulty levels
            difficultyEasy: 'Easy',
            difficultyEasyDesc: 'Simple ingredients, quick preparation',
            difficultyMedium: 'Medium',
            difficultyMediumDesc: 'Some preparation required',
            difficultyHard: 'Hard',
            difficultyHardDesc: 'Complex process or rare ingredients',
            // Edit mode
            editTitle: 'Edit Remedy',
            editSubtitle: 'Update your remedy details',
            updateRemedy: 'Update Remedy',
            updating: 'Updating...',
            updateSuccess: 'Remedy updated successfully!',
            updateError: 'Failed to update remedy',
            // Delete
            deleteRemedy: 'Delete Remedy',
            deleteConfirmTitle: 'Delete This Remedy?',
            deleteConfirmMessage: 'This action cannot be undone. Are you sure you want to delete this remedy?',
            deleteSuccess: 'Remedy deleted successfully',
            deleteError: 'Failed to delete remedy',
            cancel: 'Cancel',
            confirm: 'Confirm',
            // Validation
            descriptionMinLength: 'Description must be at least 20 characters',
        },
        
        // Dashboard Categories
        dashboardCategories: {
            all: 'All Remedies',
            digestive: 'Digestive',
            respiratory: 'Respiratory',
            skinCare: 'Skin Care',
            immunity: 'Immunity',
            stressRelief: 'Stress Relief',
            sleep: 'Sleep',
            hairCare: 'Hair Care',
            painRelief: 'Pain Relief',
            energyBooster: 'Energy Booster',
        },
        
        // Remedy Detail Page extras
        remedyDetail: {
            noInstructions: 'No instructions available',
            noIngredients: 'No ingredients listed',
            noBenefits: 'No benefits listed',
            noPrecautions: 'No precautions listed',
            preparationSteps: 'Preparation Steps',
            instructions: 'Instructions',
            resetProgress: 'Reset Progress',
            allDone: 'All Done!',
            remedyReady: 'Your remedy is ready. Enjoy the natural healing benefits!',
            tip: 'Tip',
            stepNumber: 'Step',
            minutesShort: 'min',
            servings: 'Servings',
            bestTime: 'Best Time',
            origin: 'Origin',
            suitable: 'Suitable For',
            notSuitable: 'Not Suitable For',
            adults: 'Adults',
            children: 'Children',
            elderly: 'Elderly',
            pregnant: 'Pregnant Women',
            nursing: 'Nursing Mothers',
        },
        
        // AI Assistant
        ai: {
            title: 'AI Assistant',
            pageTitle: 'AI Health Assistant',
            subtitle: 'Your personal natural remedy guide',
            poweredBy: 'Powered by AI • Natural Remedies Expert',
            naturalRemediesExpert: 'Natural Remedies Expert',
            placeholder: 'Ask about natural remedies, health tips, or wellness advice...',
            askMeAnything: 'Ask me anything...',
            loginToChat: 'Log in to chat...',
            thinking: 'Thinking...',
            welcomeMessage: "Hi! 👋 I'm your **Homely AI Assistant**. Ask me about natural remedies, herbal teas, or wellness tips!",
            suggestedQuestions: 'Try asking:',
            voiceInput: 'Voice input',
            send: 'Send',
            newChat: 'New Chat',
            fullScreen: 'Full Screen',
            openFullPage: 'Open Full Page',
            minimize: 'Minimize',
            expand: 'Expand',
            close: 'Close',
            clearChat: 'Clear Chat',
            medicalDisclaimer: 'Medical Disclaimer',
            disclaimer: 'Disclaimer',
            disclaimerText: 'This AI provides general wellness information only. It is not a substitute for professional medical advice, diagnosis, or treatment.',
            dismiss: 'Dismiss',
            enterToSend: 'Press Enter to send, Shift+Enter for new line',
            loginRequired: 'Please log in to use the AI Assistant',
            loginToChatWith: 'to chat with the AI Assistant.',
            // Suggested questions
            coldFlu: 'Natural remedies for cold and flu',
            sleepQuality: 'How to improve sleep quality naturally',
            headache: 'Home remedies for headache',
            herbalTeas: 'Best herbal teas for digestion',
            skinCare: 'Natural skin care tips',
            immunity: 'Boost immunity naturally',
            chatCleared: 'Chat cleared',
            // Voice input translations
            voiceNotSupported: 'Voice input is not supported in your browser. Try Chrome or Edge.',
            microphoneDenied: 'Microphone access denied. Please allow microphone access.',
            voiceError: 'Voice input error. Please try again.',
            voiceRecorded: 'Voice recorded successfully!',
            voiceStartFailed: 'Failed to start voice input. Please try again.',
            listeningInLanguage: 'Listening in {lang}... Speak now! Click mic to stop.',
            // Image translations
            analyzeImage: 'Please analyze this image and tell me what you see.',
            imageAttached: 'Image attached! I can analyze this image for you.',
            fileTooLarge: 'File too large. Maximum size is 5MB.',
            unsupportedFileType: 'Unsupported file type. Please upload images or PDF files.',
            // Rate limiting translations
            requestsRemaining: 'requests remaining today',
            unlimitedRequests: 'Unlimited requests',
            limitReached: 'Daily limit reached',
            limitReachedDesc: 'You have used all 5 AI requests for today. Limit resets at midnight.',
        },
        
        // Profile
        profile: {
            title: 'My Profile',
            editProfile: 'Edit Profile',
            myRemedies: 'My Remedies',
            savedRemedies: 'Saved Remedies',
            followers: 'Followers',
            following: 'Following',
            memberSince: 'Member since',
            bio: 'Bio',
            location: 'Location',
            website: 'Website',
            noRemedies: 'No remedies yet',
            noSaved: 'No saved remedies',
            changePicture: 'Change Picture',
            changeBanner: 'Change Banner',
            deleteAccount: 'Delete Account',
            overview: 'Overview',
            favorites: 'Favorites',
            noFavorites: 'No favorites yet',
            reviewsGiven: 'Reviews Given',
            remediesCreated: 'Remedies Created',
            profileInformation: 'Profile Information',
            edit: 'Edit',
            fullName: 'Full Name',
            email: 'Email',
            emailAddress: 'Email Address',
            emailCannotChange: 'Email cannot be changed',
            saveChanges: 'Save Changes',
            cancel: 'Cancel',
            noSavedDescription: 'Save remedies to access them quickly later',
            browseRemedies: 'Browse Remedies',
            noFavoritesDescription: 'Mark remedies as favorites to see them here',
            discoverRemedies: 'Discover Remedies',
            loadingProfile: 'Loading profile...',
            clickToEditBanner: 'Click to edit banner',
            delete: 'Delete',
            crop: 'Crop',
            banner: 'Banner',
            image: 'Image',
            zoom: 'Zoom',
            save: 'Save',
            accountInfo: 'Account Information',
            noMyRemedies: 'No remedies created yet',
            noMyRemediesDescription: 'Create remedies to share your knowledge with others',
            createRemedy: 'Create Remedy',
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
        },
        
        // Admin Panel
        admin: {
            title: 'Admin Panel',
            subtitle: 'Manage pending remedy submissions',
            pendingRemedies: 'pending remedies awaiting review',
            pendingRemedy: 'pending remedy awaiting review',
            refresh: 'Refresh',
            allCaughtUp: 'All caught up!',
            noPendingRemedies: 'No pending remedies to review.',
            approve: 'Approve',
            reject: 'Reject',
            approveSuccess: 'Remedy approved successfully!',
            rejectSuccess: 'Remedy rejected',
            rejectRemedy: 'Reject Remedy',
            rejectionReason: 'Please provide a reason for rejection (optional):',
            rejectionPlaceholder: 'e.g., Incomplete information, safety concerns...',
            confirmReject: 'Confirm Reject',
            cancel: 'Cancel',
            accessDenied: 'Access denied. Admin only.',
            createdBy: 'Created by',
            unknownUser: 'Unknown',
        },
        
        // Settings
        settings: {
            title: 'Settings',
            subtitle: 'Manage your preferences',
            language: 'Language',
            languageDesc: 'Choose your preferred language',
            english: 'English',
            hindi: 'हिंदी (Hindi)',
            kannada: 'ಕನ್ನಡ (Kannada)',
            theme: 'Theme',
            themeDesc: 'Choose light or dark mode',
            lightMode: 'Light Mode',
            darkMode: 'Dark Mode',
            switchToLight: 'Switch to light mode',
            switchToDark: 'Switch to dark mode',
            systemDefault: 'System Default',
            notifications: 'Notifications',
            notificationsDesc: 'Manage your notification preferences',
            emailNotifications: 'Email Notifications',
            pushNotifications: 'Push Notifications',
            privacy: 'Privacy',
            privacyDesc: 'Control your privacy settings',
            profileVisibility: 'Profile Visibility',
            showEmail: 'Show Email',
            account: 'Account',
            accountDesc: 'Manage your account settings',
            changePassword: 'Change Password',
            exportData: 'Export My Data',
            deleteAccount: 'Delete Account',
            saveChanges: 'Save Changes',
            saved: 'Settings saved!',
        },
        
        // Auth
        auth: {
            welcomeBack: 'Welcome Back! 👋',
            signInToContinue: 'Sign in to continue your wellness journey',
            loginTitle: 'Welcome Back',
            loginSubtitle: 'Sign in to continue your wellness journey',
            signupTitle: 'Create Account 🌿',
            signupSubtitle: 'Start your natural wellness journey today',
            email: 'Email',
            emailAddress: 'Email Address',
            emailPlaceholder: 'you@example.com',
            password: 'Password',
            passwordPlaceholder: 'Enter your password',
            createPassword: 'Create a strong password',
            confirmPassword: 'Confirm Password',
            confirmPasswordPlaceholder: 'Confirm your password',
            fullName: 'Full Name',
            fullNamePlaceholder: 'John Doe',
            rememberMe: 'Remember me',
            forgotPassword: 'Forgot password?',
            noAccount: "Don't have an account?",
            hasAccount: 'Already have an account?',
            signIn: 'Sign In',
            signUp: 'Create Account',
            signInWith: 'Sign in with',
            signUpWith: 'Sign up with',
            orContinueWith: 'or continue with',
            signUpFree: 'Sign up free',
            createAccount: 'Create your free account',
            verifyEmail: 'Verify Your Email',
            otpSent: 'We sent a code to your email',
            enterOtp: 'Enter OTP',
            resendOtp: 'Resend OTP',
            resetPassword: 'Reset Password',
            sendResetLink: 'Send Reset Link',
            newPassword: 'New Password',
            newPasswordPlaceholder: 'Enter new password',
            resetSuccess: 'Password reset successful!',
            backToLogin: 'Back to Login',
            emailRequired: 'Email is required',
            validEmail: 'Please enter a valid email',
            passwordRequired: 'Password is required',
            passwordMin: 'Password must be at least 6 characters',
            nameRequired: 'Name is required',
            nameMin: 'Name must be at least 2 characters',
            confirmPasswordRequired: 'Please confirm your password',
            passwordsMatch: 'Passwords do not match',
            enterEmailToReset: 'Enter your email to reset password',
            checkEmailForReset: 'Check your email for reset instructions',
            otpTitle: 'Verify Your Account',
            otpSubtitle: 'Enter the 6-digit code sent to',
            verifyOtp: 'Verify OTP',
            resendIn: 'Resend in',
            seconds: 'seconds',
            startJourney: 'Start Your Wellness Journey ✨',
            joinCommunity: 'Join our community of natural health enthusiasts',
            atLeast6Chars: 'At least 6 characters',
            oneUppercase: 'One uppercase letter',
            oneLowercase: 'One lowercase letter',
            oneNumber: 'One number',
            termsAgree: 'By creating an account, you agree to our',
            termsOfService: 'Terms of Service',
            and: 'and',
            privacyPolicy: 'Privacy Policy',
            loginRequired: 'Please log in to create a remedy',
        },
        
        // Errors
        errors: {
            required: 'This field is required',
            invalidEmail: 'Please enter a valid email',
            passwordMin: 'Password must be at least 8 characters',
            passwordMatch: 'Passwords do not match',
            somethingWrong: 'Something went wrong',
            tryAgain: 'Please try again',
            notFound: 'Page not found',
            unauthorized: 'Please log in to continue',
            forbidden: 'You do not have permission',
        },
        
        // Success messages
        success: {
            remedyCreated: 'Remedy created successfully!',
            remedyUpdated: 'Remedy updated successfully!',
            remedyDeleted: 'Remedy deleted successfully!',
            profileUpdated: 'Profile updated successfully!',
            passwordChanged: 'Password changed successfully!',
            loggedOut: 'Logged out successfully!',
            welcomeBackUser: 'Welcome back, {name}!',
            accountCreated: 'Account created successfully!',
            registrationSuccessful: 'Registration successful! Please verify your email.',
            pleaseVerifyEmail: 'Please verify your email to continue.',
            emailVerified: 'Email verified successfully!',
            otpSentSuccess: 'OTP sent successfully!',
            passwordResetOtpSent: 'Password reset OTP sent to your email.',
            passwordResetSuccess: 'Password reset successful! Please login.',
        },
        
        // Footer
        footer: {
            description: 'Preserving traditional wisdom for modern wellness. Discover natural home remedies passed down through generations.',
            explore: 'Explore',
            allRemedies: 'All Remedies',
            categories: 'Categories',
            popular: 'Popular',
            latest: 'Latest',
            categoriesTitle: 'Categories',
            immunityBoosters: 'Immunity Boosters',
            digestiveHealth: 'Digestive Health',
            coldAndCough: 'Cold & Cough',
            skinCare: 'Skin Care',
            support: 'Support',
            aboutUs: 'About Us',
            contact: 'Contact',
            faq: 'FAQ',
            privacyPolicy: 'Privacy Policy',
            copyright: 'All rights reserved.',
            madeWith: 'Made with',
            forWellness: 'for wellness',
            terms: 'Terms',
            privacy: 'Privacy',
            cookies: 'Cookies',
        },
        
        // About Page
        about: {
            title: 'About Us',
            subtitle: 'Discover the story behind Homely and our mission to preserve traditional wellness knowledge.',
            storyTitle: 'Our Story',
            storyP1: 'Homely was born from a simple belief: the wisdom of our grandparents deserves to be preserved and shared. In a world increasingly dependent on synthetic solutions, we wanted to create a space where traditional home remedies could thrive.',
            storyP2: 'Our platform brings together centuries of natural healing knowledge, making it accessible to everyone. From immunity boosters to skin care, we curate and verify remedies that have stood the test of time.',
            naturalTitle: '100% Natural',
            naturalDesc: 'All remedies use natural ingredients that you can find in your kitchen or local market.',
            communityTitle: 'Community Driven',
            communityDesc: 'Our users share and rate remedies, creating a trusted knowledge base.',
            missionTitle: 'Our Mission',
            missionDesc: 'To make traditional wellness knowledge accessible to everyone, everywhere.',
            careTitle: 'Made with Care',
            careDesc: 'Every remedy is reviewed for safety and effectiveness before being published.',
            teamTitle: 'Built with Passion',
            teamDesc: 'Homely is a passion project dedicated to preserving the healing traditions of our ancestors for future generations.',
        },
        
        // Contact Page
        contact: {
            title: 'Contact Us',
            subtitle: 'Have questions or suggestions? We\'d love to hear from you!',
            formTitle: 'Send us a Message',
            nameLabel: 'Your Name',
            namePlaceholder: 'John Doe',
            emailLabel: 'Email Address',
            emailPlaceholder: 'you@example.com',
            subjectLabel: 'Subject',
            subjectPlaceholder: 'How can we help?',
            messageLabel: 'Message',
            messagePlaceholder: 'Tell us what\'s on your mind...',
            sendButton: 'Send Message',
            successMessage: 'Message sent successfully! We\'ll get back to you soon.',
            infoTitle: 'Get in Touch',
            email: 'Email',
            location: 'Location',
            followUs: 'Follow Us',
        },
        
        // FAQ Page
        faq: {
            title: 'Frequently Asked Questions',
            subtitle: 'Find answers to common questions about Homely and natural remedies.',
            searchPlaceholder: 'Search questions...',
            q1: 'What is Homely?',
            a1: 'Homely is a platform dedicated to preserving and sharing traditional home remedies. We bring together natural healing solutions that have been passed down through generations.',
            q2: 'Are these remedies safe to use?',
            a2: 'While traditional remedies are generally safe, we always recommend consulting with a healthcare professional before trying any new treatment, especially if you have existing health conditions or are taking medications.',
            q3: 'Can I contribute my own remedies?',
            a3: 'Yes! Once you create an account, you can share your family\'s traditional remedies with our community. All submissions are reviewed before being published.',
            q4: 'Is Homely free to use?',
            a4: 'Yes, Homely is completely free to use. You can browse, search, and save remedies without any cost.',
            q5: 'How do I save my favorite remedies?',
            a5: 'Simply click the bookmark icon on any remedy card to save it to your favorites. You can access your saved remedies from your profile.',
            q6: 'What is the AI Assistant?',
            a6: 'Our AI Assistant can help you find remedies based on your symptoms or health goals. It uses natural language processing to understand your needs and suggest relevant remedies.',
            q7: 'How can I report an issue with a remedy?',
            a7: 'If you find any incorrect or potentially harmful information, please use the contact form to report it. We take safety very seriously.',
            q8: 'Is my personal data safe?',
            a8: 'Yes, we take privacy seriously. We only collect essential information and never share your data with third parties. See our Privacy Policy for details.',
            noResults: 'No questions found matching your search.',
            stillHaveQuestions: 'Still have questions?',
            contactUs: 'Can\'t find what you\'re looking for? Reach out to us directly.',
            getInTouch: 'Get in Touch',
        },
        
        // Privacy Page
        privacy: {
            title: 'Privacy Policy',
            subtitle: 'Your privacy matters to us. Learn how we collect, use, and protect your information.',
            lastUpdated: 'Last Updated',
            intro: 'At Homely, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our platform.',
            dataCollectionTitle: 'Information We Collect',
            dataCollectionContent: 'We collect information you provide directly, such as your name, email address, and any remedies you create. We also automatically collect certain information about your device and how you interact with our platform.',
            dataUsageTitle: 'How We Use Your Information',
            dataUsageContent: 'We use your information to provide and improve our services, personalize your experience, communicate with you about updates and features, and ensure the security of our platform.',
            dataProtectionTitle: 'How We Protect Your Data',
            dataProtectionContent: 'We implement industry-standard security measures to protect your data, including encryption, secure servers, and regular security audits. We never sell your personal information to third parties.',
            yourRightsTitle: 'Your Rights',
            yourRightsContent: 'You have the right to access, correct, or delete your personal data at any time. You can also request a copy of your data or ask us to stop processing it.',
            questionsTitle: 'Questions?',
            questionsContent: 'If you have any questions about our privacy practices, please contact us.',
        },
        
        // Terms Page
        terms: {
            title: 'Terms of Service',
            subtitle: 'Please read these terms carefully before using Homely.',
            lastUpdated: 'Last Updated',
            intro: 'By using Homely, you agree to these Terms of Service. If you do not agree with any part of these terms, please do not use our platform.',
            acceptanceTitle: 'Acceptance of Terms',
            acceptanceContent: 'By accessing or using Homely, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.',
            userAccountsTitle: 'User Accounts',
            userAccountsContent: 'You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information and to update it as needed. We reserve the right to suspend or terminate accounts that violate our terms.',
            contentTitle: 'User Content',
            contentContent: 'You retain ownership of content you create on Homely. By posting remedies or other content, you grant us a non-exclusive license to display and share that content on our platform. You agree not to post harmful, misleading, or illegal content.',
            disclaimerTitle: 'Medical Disclaimer',
            disclaimerContent: 'Homely provides information for educational purposes only. The remedies shared on our platform are not intended to replace professional medical advice. Always consult a healthcare provider before trying any new treatment.',
            changesTitle: 'Changes to Terms',
            changesContent: 'We may update these terms from time to time. We will notify you of significant changes via email or platform notification. Continued use after changes constitutes acceptance of the new terms.',
        },
        
        // Cookies Page
        cookies: {
            title: 'Cookie Policy',
            subtitle: 'Learn how we use cookies to improve your experience on Homely.',
            whatAreTitle: 'What Are Cookies?',
            whatAreContent: 'Cookies are small text files stored on your device when you visit a website. They help us remember your preferences and understand how you use our platform.',
            typesTitle: 'Types of Cookies We Use',
            essentialTitle: 'Essential Cookies',
            essentialDesc: 'These cookies are necessary for the website to function properly. They enable core features like user authentication and security.',
            functionalTitle: 'Functional Cookies',
            functionalDesc: 'These cookies remember your preferences, such as language selection and theme settings, to provide a personalized experience.',
            analyticsTitle: 'Analytics Cookies',
            analyticsDesc: 'We use these cookies to understand how visitors interact with our website, helping us improve our services.',
            required: 'Required',
            optional: 'Optional',
            manageTitle: 'Managing Cookies',
            manageContent: 'You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of our website.',
            questionsTitle: 'Questions?',
            questionsContent: 'If you have questions about our cookie practices, please contact us.',
        },
        
        // Popular Page
        popular: {
            title: 'Popular Remedies',
            subtitle: 'Discover the most loved remedies in our community.',
            remediesFound: 'popular remedies',
            loginRequired: 'Login Required',
            loginToView: 'Please log in to view popular remedies.',
            noRemedies: 'No Popular Remedies',
            noRemediesDesc: 'Check back later for trending remedies.',
        },
        
        // Latest Page
        latest: {
            title: 'Latest Remedies',
            subtitle: 'Explore the newest additions to our remedy collection.',
            remediesFound: 'new remedies',
            loginRequired: 'Login Required',
            loginToView: 'Please log in to view the latest remedies.',
            noRemedies: 'No New Remedies',
            noRemediesDesc: 'Be the first to share a remedy!',
        },
        
        // 404 Page
        notFound: {
            title: '404',
            subtitle: 'Page Not Found',
            description: "Oops! Looks like this remedy got lost in the garden. Let's get you back to familiar territory.",
            backHome: 'Back to Home',
            goBack: 'Go Back',
        },
    },
    
    hi: {
        // Common
        appName: 'होमली',
        tagline: 'आधुनिक जीवन के लिए प्राकृतिक उपचार',
        loading: 'लोड हो रहा है...',
        save: 'सहेजें',
        cancel: 'रद्द करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        submit: 'सबमिट करें',
        search: 'खोजें',
        close: 'बंद करें',
        back: 'वापस',
        next: 'आगे',
        yes: 'हाँ',
        no: 'नहीं',
        or: 'या',
        and: 'और',
        
        // Navigation
        nav: {
            home: 'होम',
            dashboard: 'डैशबोर्ड',
            aiAssistant: 'AI सहायक',
            createRemedy: 'उपचार बनाएं',
            profile: 'प्रोफाइल',
            settings: 'सेटिंग्स',
            login: 'लॉगिन',
            signup: 'साइन अप',
            logout: 'लॉग आउट',
            please: 'कृपया',
            or: 'या',
        },
        
        // Home Page
        home: {
            heroTitle: 'प्राकृतिक खोजें',
            heroTitleHighlight: 'घरेलू उपचार',
            heroSubtitle: 'रोज़मर्रा की स्वास्थ्य समस्याओं के लिए समय-परीक्षित प्राकृतिक समाधान खोजें। आपकी स्वास्थ्य यात्रा यहाँ से शुरू होती है।',
            exploreRemedies: 'उपचार देखें',
            askAI: 'AI से पूछें',
            searchRemedies: 'उपचार खोजें',
            featuresTitle: 'होमली क्यों चुनें?',
            feature1Title: 'प्राकृतिक समाधान',
            feature1Desc: 'अपनी रसोई में मिलने वाली प्राकृतिक सामग्री से उपचार खोजें',
            feature2Title: 'AI-संचालित मदद',
            feature2Desc: 'हमारे स्मार्ट AI सहायक से व्यक्तिगत उपचार सुझाव प्राप्त करें',
            feature3Title: 'समुदाय संचालित',
            feature3Desc: 'प्राकृतिक स्वास्थ्य प्रेमियों के समुदाय से साझा करें और सीखें',
            feature4Title: 'सुरक्षित और सत्यापित',
            feature4Desc: 'सभी उपचार सुरक्षा और प्रभावशीलता के लिए समीक्षित हैं',
            forEverydayWellness: 'दैनिक स्वास्थ्य के लिए',
            badge: 'पारंपरिक ज्ञान, आधुनिक स्वास्थ्य',
            naturalRemedies: 'प्राकृतिक उपचार',
            categories: 'श्रेणियाँ',
            happyUsers: 'खुश उपयोगकर्ता',
            timeTested: 'समय-परीक्षित उपचार',
            timeTestedDesc: 'पीढ़ियों से पारंपरिक चिकित्सकों और आयुर्वेदिक विशेषज्ञों द्वारा सत्यापित सदियों पुरानी विधियों तक पहुंचें।',
            simpleIngredients: 'सरल सामग्री',
            simpleIngredientsDesc: 'हल्दी, अदरक और शहद जैसी रोज़मर्रा की रसोई सामग्री का उपयोग करके शक्तिशाली प्राकृतिक उपचार बनाएं।',
            safeNatural: 'सुरक्षित और प्राकृतिक',
            safeNaturalDesc: 'हानिकारक रसायनों के बिना कोमल उपचार। प्रत्येक उपचार में सुरक्षा दिशानिर्देश और सावधानियां शामिल हैं।',
            aiAssistant: 'AI स्वास्थ्य सहायक',
            aiAssistantDesc: 'पारंपरिक चिकित्सा ज्ञान पर प्रशिक्षित हमारे बुद्धिमान सहायक से व्यक्तिगत मार्गदर्शन प्राप्त करें।',
            detailedInstructions: 'विस्तृत निर्देश',
            detailedInstructionsDesc: 'सामग्री सूची, समय और खुराक अनुशंसाओं के साथ चरण-दर-चरण तैयारी गाइड।',
            communityWisdom: 'समुदाय ज्ञान',
            communityWisdomDesc: 'अपने अनुभव और पारंपरिक ज्ञान साझा करने वाले हजारों लोगों से जुड़ें।',
            howItWorks: 'यह कैसे काम करता है',
            step1Title: 'उपचार ब्राउज़ करें',
            step1Desc: 'सामान्य बीमारियों के लिए प्राकृतिक उपचारों के हमारे संग्रह का अन्वेषण करें।',
            step2Title: 'सामग्री प्राप्त करें',
            step2Desc: 'अधिकांश सामग्री पहले से आपकी रसोई में हैं या आसानी से उपलब्ध हैं।',
            step3Title: 'निर्देशों का पालन करें',
            step3Desc: 'सरल चरण-दर-चरण गाइड तैयारी को आसान बनाते हैं।',
            step4Title: 'बेहतर महसूस करें',
            step4Desc: 'प्राकृतिक उपचारों की उपचार शक्ति का अनुभव करें।',
            testimonials: 'हमारे उपयोगकर्ता क्या कहते हैं',
            testimonialsSubtitle: 'उन हजारों लोगों से जुड़ें जिन्होंने प्राकृतिक उपचारों की शक्ति की खोज की है',
            communityStories: 'समुदाय की कहानियाँ',
            whatCommunitySays: 'हमारा समुदाय क्या कहता है',
            realExperiences: 'प्राकृतिक उपचारों का उपयोग करने वाले वास्तविक लोगों के वास्तविक अनुभव',
            beFirstToShare: 'अपना अनुभव साझा करने वाले पहले बनें!',
            communityGettingStarted: 'हमारा समुदाय अभी शुरू हो रहा है। बताएं कि प्राकृतिक उपचारों ने आपकी कैसे मदद की और दूसरों को उनकी स्वास्थ्य यात्रा में प्रेरित करें।',
            writeTestimonial: 'प्रशंसापत्र लिखें',
            loginToShare: 'अपनी कहानी साझा करने के लिए लॉगिन करें',
            shareYourExperience: 'अपना अनुभव साझा करें',
            updateYourTestimonial: 'अपना प्रशंसापत्र अपडेट करें',
            yourRating: 'आपकी रेटिंग',
            yourTestimonial: 'आपका प्रशंसापत्र',
            testimonialPlaceholder: 'बताएं कि होमली ने आपकी स्वास्थ्य यात्रा में कैसे मदद की है...',
            characters: 'अक्षर',
            submitting: 'सबमिट हो रहा है...',
            submitTestimonial: 'प्रशंसापत्र सबमिट करें',
            updateTestimonial: 'प्रशंसापत्र अपडेट करें',
            testimonialUpdated: 'प्रशंसापत्र सफलतापूर्वक अपडेट किया गया!',
            thankYouTestimonial: 'आपके प्रशंसापत्र के लिए धन्यवाद!',
            editMyTestimonial: 'मेरा प्रशंसापत्र संपादित करें',
            shareYourStory: 'अपनी कहानी साझा करें',
            ctaTitle: 'अपनी प्राकृतिक स्वास्थ्य यात्रा शुरू करें',
            ctaSubtitle: 'उन हजारों उपयोगकर्ताओं से जुड़ें जो अपनी स्वास्थ्य जरूरतों के लिए होमली पर भरोसा करते हैं।',
            getStartedFree: 'मुफ्त शुरू करें',
            learnMore: 'और जानें',
            popularCategories: 'लोकप्रिय श्रेणियाँ',
            viewAll: 'सभी देखें',
            readyToStart: 'शुरू करने के लिए तैयार?',
            joinCommunity: 'प्राकृतिक स्वास्थ्य उत्साहियों के हमारे समुदाय से जुड़ें',
            signUpFree: 'मुफ्त साइन अप करें',
            freeForever: 'हमेशा मुफ्त',
            safe: '100% सुरक्षित',
            noAds: 'कोई विज्ञापन नहीं',
            alreadyMember: 'पहले से सदस्य हैं?',
            signIn: 'साइन इन करें',
        },
        
        // Dashboard
        dashboard: {
            title: 'प्राकृतिक उपचार खोजें',
            subtitle: 'विभिन्न स्वास्थ्य समस्याओं के लिए समय-परीक्षित घरेलू उपचारों का संग्रह देखें',
            searchPlaceholder: 'नाम, सामग्री, या स्थिति से उपचार खोजें...',
            voiceSearch: 'आवाज से खोजें',
            addRemedy: 'उपचार जोड़ें',
            noResults: 'कोई उपचार नहीं मिला',
            noResultsDesc: 'अपनी खोज या फ़िल्टर बदलकर देखें',
            clearFilters: 'फ़िल्टर साफ़ करें',
            sortBy: 'क्रमबद्ध करें',
            newest: 'नवीनतम पहले',
            oldest: 'पुराना पहले',
            popular: 'सबसे लोकप्रिय',
            highestRated: 'उच्चतम रेटिंग',
            alphabetical: 'A-Z',
            remediesFound: 'उपचार मिले',
            loadMore: 'और उपचार लोड करें',
            showing: 'दिखा रहे हैं',
            of: 'में से',
            guestBanner: 'आप नमूना उपचार देख रहे हैं। सभी सुविधाओं को अनलॉक करने के लिए साइन इन करें!',
            signInNow: 'अभी साइन इन करें',
            loginRequired: 'लॉगिन आवश्यक',
            loginRequiredDesc: 'पूर्ण उपचार विवरण देखने, पसंदीदा सहेजने और सभी सुविधाओं तक पहुंचने के लिए साइन इन करें!',
            loginToSearch: 'उपचार खोजने के लिए कृपया लॉगिन करें',
            loginToFilter: 'श्रेणी के अनुसार फ़िल्टर करने के लिए कृपया लॉगिन करें',
            loginToSort: 'क्रमबद्ध करने के लिए कृपया लॉगिन करें',
            loginToSave: 'उपचार सहेजने के लिए कृपया लॉगिन करें',
            remedySaved: 'उपचार सहेजा गया!',
            maybeLater: 'बाद में',
        },
        
        // Categories
        categories: {
            all: 'सभी',
            immunityBooster: 'इम्यूनिटी',
            digestiveHealth: 'पाचन',
            respiratoryHealth: 'श्वसन',
            skinCare: 'त्वचा देखभाल',
            hairCare: 'बाल देखभाल',
            painRelief: 'दर्द निवारण',
            stressRelief: 'तनाव मुक्ति',
            sleepAid: 'नींद सहायक',
            energyBoost: 'ऊर्जा',
            detox: 'डिटॉक्स',
            weightManagement: 'वजन',
            heartHealth: 'हृदय स्वास्थ्य',
            boneJointHealth: 'हड्डी और जोड़',
            eyeHealth: 'आँख स्वास्थ्य',
            oralHealth: 'मौखिक स्वास्थ्य',
            womensHealth: 'महिला स्वास्थ्य',
            mensHealth: 'पुरुष स्वास्थ्य',
            childHealth: 'बाल स्वास्थ्य',
            elderCare: 'बुजुर्ग देखभाल',
            other: 'अन्य',
            // Categories Page
            title: 'श्रेणियाँ ब्राउज़ करें',
            subtitle: 'स्वास्थ्य चिंता के अनुसार व्यवस्थित प्राकृतिक उपचारों का हमारा संग्रह खोजें।',
            immunityBoosterDesc: 'प्राकृतिक उपचारों से अपनी प्रतिरक्षा प्रणाली को मजबूत करें।',
            digestiveHealthDesc: 'बेहतर पाचन के लिए उपचार।',
            coldAndCough: 'सर्दी और खांसी',
            coldAndCoughDesc: 'सर्दी और खांसी के लक्षणों से प्राकृतिक राहत।',
            skinCareDesc: 'स्वस्थ, चमकदार त्वचा के लिए प्राकृतिक समाधान।',
            hairCareDesc: 'मजबूत, स्वस्थ बालों के लिए पारंपरिक उपचार।',
            stressReliefDesc: 'तनाव और चिंता को कम करने के प्राकृतिक तरीके।',
            energyVitality: 'ऊर्जा और जीवन शक्ति',
            energyVitalityDesc: 'स्वाभाविक रूप से अपनी ऊर्जा के स्तर को बढ़ाएं।',
            generalWellness: 'सामान्य स्वास्थ्य',
            generalWellnessDesc: 'समग्र स्वास्थ्य और कल्याण उपचार।',
            explore: 'खोजें',
            backToCategories: 'श्रेणियों पर वापस जाएं',
            remediesFound: 'उपचार मिले',
            loginRequired: 'लॉगिन आवश्यक',
            loginToView: 'इस श्रेणी में उपचार देखने के लिए कृपया लॉग इन करें।',
            noRemedies: 'अभी तक कोई उपचार नहीं',
            noRemediesDesc: 'इस श्रेणी में पहला उपचार जोड़ें!',
            createFirst: 'पहला उपचार बनाएं',
        },
        
        // Remedy Detail
        remedy: {
            ingredients: 'सामग्री',
            instructions: 'निर्देश',
            reviews: 'समीक्षाएं',
            preparation: 'तैयारी',
            benefits: 'लाभ',
            precautions: 'सावधानियाँ',
            warnings: 'चेतावनियाँ',
            dosage: 'खुराक',
            bestTime: 'उपयोग का सबसे अच्छा समय',
            difficulty: 'कठिनाई',
            prepTime: 'तैयारी समय',
            servings: 'परोसना',
            minutes: 'मिनट',
            easy: 'आसान',
            medium: 'मध्यम',
            hard: 'कठिन',
            shareRemedy: 'साझा करें',
            saveRemedy: 'उपचार सहेजें',
            print: 'प्रिंट करें',
            like: 'पसंद करें',
            liked: 'पसंद किया',
            reportRemedy: 'रिपोर्ट करें',
            createdBy: 'निर्माता',
            sharedBy: 'साझा किया',
            communityMember: 'समुदाय सदस्य',
            askAboutRemedy: 'इस उपचार के बारे में AI से पूछें',
            notFound: 'उपचार नहीं मिला',
            notFoundDesc: 'आप जो उपचार ढूंढ रहे हैं वह मौजूद नहीं है।',
            backToDashboard: 'डैशबोर्ड पर वापस जाएं',
            loadError: 'उपचार लोड करने में विफल। कृपया पुनः प्रयास करें।',
            savedToCollection: 'संग्रह में सहेजा गया!',
            removedFromSaved: 'सहेजे गए से हटाया गया',
            saveError: 'उपचार सहेजने में विफल',
            loginToLike: 'पसंद करने के लिए कृपया लॉगिन करें',
            loginToRate: 'रेट करने के लिए कृपया लॉगिन करें',
            thanksForRating: 'रेटिंग के लिए धन्यवाद!',
            ratingError: 'रेटिंग सबमिट करने में विफल',
            linkCopied: 'लिंक क्लिपबोर्ड पर कॉपी किया गया!',
            rateThisRemedy: 'इस उपचार को रेट करें',
            youRated: 'आपने रेट किया',
            stars: 'सितारे',
            clickToRate: 'रेट करने के लिए क्लिक करें',
            noReviews: 'अभी तक कोई समीक्षा नहीं।',
            beFirstToReview: 'अपना अनुभव साझा करने वाले पहले बनें!',
            anonymous: 'अनाम',
        },
        
        // Create Remedy
        create: {
            title: 'नया उपचार बनाएं',
            editTitle: 'उपचार संपादित करें',
            basicInfo: 'मूल जानकारी',
            remedyTitle: 'शीर्षक',
            titlePlaceholder: 'जैसे, सर्दी के लिए अदरक शहद चाय',
            description: 'विवरण',
            descPlaceholder: 'उपचार का संक्षिप्त विवरण...',
            category: 'श्रेणी',
            selectCategory: 'श्रेणी चुनें',
            addIngredient: 'सामग्री जोड़ें',
            addStep: 'चरण जोड़ें',
            ingredientName: 'सामग्री',
            quantity: 'मात्रा',
            unit: 'इकाई',
            optional: 'वैकल्पिक',
            stepInstruction: 'निर्देश',
            duration: 'अवधि',
            tips: 'सुझाव',
            publishRemedy: 'उपचार प्रकाशित करें',
            saveDraft: 'ड्राफ्ट सहेजें',
            aiAssist: 'AI सहायता',
        },
        
        // Create Remedy Page (detailed)
        createRemedy: {
            title: 'नया उपचार बनाएं',
            subtitle: 'समुदाय के साथ अपना प्राकृतिक उपचार साझा करें',
            basicInfo: 'मूल जानकारी',
            name: 'उपचार का नाम',
            namePlaceholder: 'जैसे, सर्दी के लिए शहद अदरक चाय',
            description: 'विवरण',
            descriptionPlaceholder: 'अपने उपचार और इसके लाभों का वर्णन करें...',
            category: 'श्रेणी',
            difficulty: 'कठिनाई स्तर',
            preparationTime: 'तैयारी का समय (मिनट)',
            optional: 'वैकल्पिक',
            ingredients: 'सामग्री',
            addIngredient: 'सामग्री जोड़ें',
            ingredientName: 'सामग्री का नाम',
            amount: 'मात्रा',
            unit: 'इकाई',
            steps: 'निर्देश',
            stepInstruction: 'चरण-दर-चरण निर्देश जोड़ें',
            addStep: 'चरण जोड़ें',
            stepPlaceholder: 'इस चरण का वर्णन करें...',
            tips: 'अतिरिक्त जानकारी',
            benefits: 'लाभ',
            addBenefit: 'लाभ जोड़ें',
            benefitPlaceholder: 'जैसे, प्रतिरक्षा बढ़ाता है',
            warnings: 'चेतावनी और सावधानियां',
            addWarning: 'चेतावनी जोड़ें',
            warningPlaceholder: 'जैसे, गर्भावस्था में अनुशंसित नहीं',
            tags: 'टैग',
            addTag: 'टैग जोड़ें',
            tagPlaceholder: 'जैसे, प्रतिरक्षा, सर्दी, प्राकृतिक',
            imageUrl: 'छवि URL',
            publishRemedy: 'उपचार प्रकाशित करें',
            publishing: 'प्रकाशित हो रहा है...',
            saveDraft: 'ड्राफ्ट सहेजें',
            next: 'अगला',
            previous: 'पिछला',
            step: 'चरण',
            of: 'का',
            reviewSubmit: 'समीक्षा और सबमिट करें',
            reviewInfo: 'प्रकाशित करने से पहले अपने उपचार की समीक्षा करें',
            remedySummary: 'उपचार सारांश',
            summaryTitle: 'शीर्षक',
            items: 'आइटम',
            stepsCount: 'चरण',
            success: 'उपचार सफलतापूर्वक बनाया गया!',
            error: 'उपचार बनाने में विफल',
            categoryDigestive: 'पाचन स्वास्थ्य',
            categoryRespiratory: 'श्वसन',
            categorySkin: 'त्वचा देखभाल',
            categoryImmunity: 'प्रतिरक्षा',
            categorySleep: 'नींद और आराम',
            categoryPain: 'दर्द निवारण',
            categoryHair: 'बाल देखभाल',
            categoryEnergy: 'ऊर्जा और जीवन शक्ति',
            categoryStress: 'तनाव राहत',
            categoryOther: 'अन्य',
            difficultyEasy: 'आसान',
            difficultyEasyDesc: 'सरल सामग्री, त्वरित तैयारी',
            difficultyMedium: 'मध्यम',
            difficultyMediumDesc: 'कुछ तैयारी आवश्यक',
            difficultyHard: 'कठिन',
            difficultyHardDesc: 'जटिल प्रक्रिया या दुर्लभ सामग्री',
            // Edit mode
            editTitle: 'उपचार संपादित करें',
            editSubtitle: 'अपने उपचार का विवरण अपडेट करें',
            updateRemedy: 'उपचार अपडेट करें',
            updating: 'अपडेट हो रहा है...',
            updateSuccess: 'उपचार सफलतापूर्वक अपडेट हुआ!',
            updateError: 'उपचार अपडेट करने में विफल',
            // Delete
            deleteRemedy: 'उपचार हटाएं',
            deleteConfirmTitle: 'इस उपचार को हटाएं?',
            deleteConfirmMessage: 'यह क्रिया पूर्ववत नहीं की जा सकती। क्या आप वाकई इस उपचार को हटाना चाहते हैं?',
            deleteSuccess: 'उपचार सफलतापूर्वक हटाया गया',
            deleteError: 'उपचार हटाने में विफल',
            cancel: 'रद्द करें',
            confirm: 'पुष्टि करें',
            // Validation
            descriptionMinLength: 'विवरण कम से कम 20 वर्णों का होना चाहिए',
        },
        
        // Dashboard Categories
        dashboardCategories: {
            all: 'सभी उपचार',
            digestive: 'पाचन',
            respiratory: 'श्वसन',
            skinCare: 'त्वचा देखभाल',
            immunity: 'प्रतिरक्षा',
            stressRelief: 'तनाव राहत',
            sleep: 'नींद',
            hairCare: 'बाल देखभाल',
            painRelief: 'दर्द निवारण',
            energyBooster: 'ऊर्जा बूस्टर',
        },
        
        // Remedy Detail Page extras
        remedyDetail: {
            noInstructions: 'कोई निर्देश उपलब्ध नहीं',
            noIngredients: 'कोई सामग्री सूचीबद्ध नहीं',
            noBenefits: 'कोई लाभ सूचीबद्ध नहीं',
            noPrecautions: 'कोई सावधानियां सूचीबद्ध नहीं',
            preparationSteps: 'तैयारी के चरण',
            instructions: 'निर्देश',
            resetProgress: 'प्रगति रीसेट करें',
            allDone: 'सब हो गया!',
            remedyReady: 'आपका उपचार तैयार है। प्राकृतिक उपचार लाभ का आनंद लें!',
            tip: 'सुझाव',
            stepNumber: 'चरण',
            minutesShort: 'मिनट',
            servings: 'परोसना',
            bestTime: 'सबसे अच्छा समय',
            origin: 'उत्पत्ति',
            suitable: 'उपयुक्त',
            notSuitable: 'अनुपयुक्त',
            adults: 'वयस्क',
            children: 'बच्चे',
            elderly: 'बुजुर्ग',
            pregnant: 'गर्भवती महिलाएं',
            nursing: 'स्तनपान कराने वाली माताएं',
        },
        
        // AI Assistant
        ai: {
            title: 'AI सहायक',
            pageTitle: 'AI स्वास्थ्य सहायक',
            subtitle: 'आपका व्यक्तिगत प्राकृतिक उपचार मार्गदर्शक',
            poweredBy: 'AI द्वारा संचालित • प्राकृतिक उपचार विशेषज्ञ',
            naturalRemediesExpert: 'प्राकृतिक उपचार विशेषज्ञ',
            placeholder: 'प्राकृतिक उपचार, स्वास्थ्य सुझाव, या कल्याण सलाह के बारे में पूछें...',
            askMeAnything: 'मुझसे कुछ भी पूछें...',
            loginToChat: 'चैट करने के लिए लॉग इन करें...',
            thinking: 'सोच रहा हूँ...',
            welcomeMessage: 'नमस्ते! 👋 मैं आपका **होमली AI सहायक** हूँ। मुझसे प्राकृतिक उपचार, हर्बल चाय, या स्वास्थ्य टिप्स के बारे में पूछें!',
            suggestedQuestions: 'यह पूछकर देखें:',
            voiceInput: 'आवाज इनपुट',
            send: 'भेजें',
            newChat: 'नई चैट',
            fullScreen: 'पूर्ण स्क्रीन',
            openFullPage: 'पूर्ण पृष्ठ खोलें',
            minimize: 'छोटा करें',
            expand: 'विस्तार करें',
            close: 'बंद करें',
            clearChat: 'चैट साफ़ करें',
            medicalDisclaimer: 'चिकित्सा अस्वीकरण',
            disclaimer: 'अस्वीकरण',
            disclaimerText: 'यह AI केवल सामान्य स्वास्थ्य जानकारी प्रदान करता है। यह पेशेवर चिकित्सा सलाह, निदान या उपचार का विकल्प नहीं है।',
            dismiss: 'खारिज करें',
            enterToSend: 'भेजने के लिए Enter दबाएं, नई लाइन के लिए Shift+Enter',
            loginRequired: 'AI सहायक का उपयोग करने के लिए कृपया लॉग इन करें',
            loginToChatWith: 'AI सहायक से चैट करने के लिए।',
            // Suggested questions
            coldFlu: 'सर्दी और फ्लू के लिए प्राकृतिक उपचार',
            sleepQuality: 'नींद की गुणवत्ता स्वाभाविक रूप से कैसे सुधारें',
            headache: 'सिरदर्द के लिए घरेलू उपचार',
            herbalTeas: 'पाचन के लिए सर्वोत्तम हर्बल चाय',
            skinCare: 'प्राकृतिक त्वचा देखभाल सुझाव',
            immunity: 'प्राकृतिक रूप से प्रतिरक्षा बढ़ाएं',
            chatCleared: 'चैट साफ़ हो गई',
            // Voice input translations
            voiceNotSupported: 'आपके ब्राउज़र में आवाज इनपुट समर्थित नहीं है। Chrome या Edge का प्रयास करें।',
            microphoneDenied: 'माइक्रोफोन एक्सेस अस्वीकृत। कृपया माइक्रोफोन एक्सेस की अनुमति दें।',
            voiceError: 'आवाज इनपुट त्रुटि। कृपया पुनः प्रयास करें।',
            voiceRecorded: 'आवाज सफलतापूर्वक रिकॉर्ड हुई!',
            voiceStartFailed: 'आवाज इनपुट शुरू करने में विफल। कृपया पुनः प्रयास करें।',
            listeningInLanguage: '{lang} में सुन रहा हूँ... अब बोलें! रोकने के लिए माइक पर क्लिक करें।',
            // Image translations
            analyzeImage: 'कृपया इस छवि का विश्लेषण करें और बताएं कि आप क्या देखते हैं।',
            imageAttached: 'छवि संलग्न! मैं इस छवि का विश्लेषण कर सकता हूँ।',
            fileTooLarge: 'फ़ाइल बहुत बड़ी है। अधिकतम आकार 5MB है।',
            unsupportedFileType: 'असमर्थित फ़ाइल प्रकार। कृपया छवियाँ या PDF फ़ाइलें अपलोड करें।',
            // Rate limiting translations
            requestsRemaining: 'आज के लिए शेष अनुरोध',
            unlimitedRequests: 'असीमित अनुरोध',
            limitReached: 'दैनिक सीमा पूरी',
            limitReachedDesc: 'आपने आज के लिए सभी 5 AI अनुरोध उपयोग कर लिए हैं। सीमा आधी रात को रीसेट होती है।',
        },
        
        // Profile
        profile: {
            title: 'मेरी प्रोफाइल',
            editProfile: 'प्रोफाइल संपादित करें',
            myRemedies: 'मेरे उपचार',
            savedRemedies: 'सहेजे गए उपचार',
            followers: 'फॉलोवर्स',
            following: 'फॉलोइंग',
            memberSince: 'सदस्य तिथि',
            bio: 'बायो',
            location: 'स्थान',
            website: 'वेबसाइट',
            noRemedies: 'अभी कोई उपचार नहीं',
            noSaved: 'कोई सहेजे गए उपचार नहीं',
            changePicture: 'तस्वीर बदलें',
            changeBanner: 'बैनर बदलें',
            deleteAccount: 'खाता हटाएं',
            overview: 'अवलोकन',
            favorites: 'पसंदीदा',
            noFavorites: 'अभी कोई पसंदीदा नहीं',
            reviewsGiven: 'दिए गए रिव्यू',
            remediesCreated: 'बनाए गए उपचार',
            profileInformation: 'प्रोफाइल जानकारी',
            edit: 'संपादित करें',
            fullName: 'पूरा नाम',
            email: 'ईमेल',
            emailAddress: 'ईमेल पता',
            emailCannotChange: 'ईमेल बदला नहीं जा सकता',
            saveChanges: 'परिवर्तन सहेजें',
            cancel: 'रद्द करें',
            noSavedDescription: 'बाद में जल्दी पहुंचने के लिए उपचार सहेजें',
            browseRemedies: 'उपचार देखें',
            noFavoritesDescription: 'उपचारों को पसंदीदा के रूप में चिह्नित करें',
            discoverRemedies: 'उपचार खोजें',
            loadingProfile: 'प्रोफाइल लोड हो रही है...',
            clickToEditBanner: 'बैनर संपादित करने के लिए क्लिक करें',
            delete: 'हटाएं',
            crop: 'काटें',
            banner: 'बैनर',
            image: 'छवि',
            zoom: 'ज़ूम',
            save: 'सहेजें',
            accountInfo: 'खाता जानकारी',
            noMyRemedies: 'अभी कोई उपचार नहीं बनाए',
            noMyRemediesDescription: 'अपना ज्ञान साझा करने के लिए उपचार बनाएं',
            createRemedy: 'उपचार बनाएं',
            pending: 'समीक्षाधीन',
            approved: 'स्वीकृत',
            rejected: 'अस्वीकृत',
        },
        
        // Admin Panel
        admin: {
            title: 'एडमिन पैनल',
            subtitle: 'लंबित उपचार सबमिशन प्रबंधित करें',
            pendingRemedies: 'लंबित उपचार समीक्षा की प्रतीक्षा में',
            pendingRemedy: 'लंबित उपचार समीक्षा की प्रतीक्षा में',
            refresh: 'रिफ्रेश करें',
            allCaughtUp: 'सब हो गया!',
            noPendingRemedies: 'समीक्षा के लिए कोई लंबित उपचार नहीं।',
            approve: 'स्वीकृत करें',
            reject: 'अस्वीकार करें',
            approveSuccess: 'उपचार सफलतापूर्वक स्वीकृत!',
            rejectSuccess: 'उपचार अस्वीकृत',
            rejectRemedy: 'उपचार अस्वीकार करें',
            rejectionReason: 'अस्वीकृति का कारण दें (वैकल्पिक):',
            rejectionPlaceholder: 'जैसे, अधूरी जानकारी, सुरक्षा चिंताएं...',
            confirmReject: 'अस्वीकृति की पुष्टि करें',
            cancel: 'रद्द करें',
            accessDenied: 'पहुंच अस्वीकृत। केवल एडमिन।',
            createdBy: 'द्वारा बनाया गया',
            unknownUser: 'अज्ञात',
        },
        
        // Settings
        settings: {
            title: 'सेटिंग्स',
            subtitle: 'अपनी प्राथमिकताएं प्रबंधित करें',
            language: 'भाषा',
            languageDesc: 'अपनी पसंदीदा भाषा चुनें',
            english: 'English (अंग्रेजी)',
            hindi: 'हिंदी',
            kannada: 'ಕನ್ನಡ (कन्नड़)',
            theme: 'थीम',
            themeDesc: 'लाइट या डार्क मोड चुनें',
            lightMode: 'लाइट मोड',
            darkMode: 'डार्क मोड',
            switchToLight: 'लाइट मोड पर स्विच करें',
            switchToDark: 'डार्क मोड पर स्विच करें',
            systemDefault: 'सिस्टम डिफ़ॉल्ट',
            notifications: 'सूचनाएं',
            notificationsDesc: 'अपनी सूचना प्राथमिकताएं प्रबंधित करें',
            emailNotifications: 'ईमेल सूचनाएं',
            pushNotifications: 'पुश सूचनाएं',
            privacy: 'गोपनीयता',
            privacyDesc: 'अपनी गोपनीयता सेटिंग्स नियंत्रित करें',
            profileVisibility: 'प्रोफाइल दृश्यता',
            showEmail: 'ईमेल दिखाएं',
            account: 'खाता',
            accountDesc: 'अपनी खाता सेटिंग्स प्रबंधित करें',
            changePassword: 'पासवर्ड बदलें',
            exportData: 'मेरा डेटा निर्यात करें',
            deleteAccount: 'खाता हटाएं',
            saveChanges: 'परिवर्तन सहेजें',
            saved: 'सेटिंग्स सहेजी गईं!',
        },
        
        // Auth
        auth: {
            welcomeBack: 'वापस स्वागत है! 👋',
            signInToContinue: 'अपनी स्वास्थ्य यात्रा जारी रखने के लिए साइन इन करें',
            loginTitle: 'वापस स्वागत है',
            loginSubtitle: 'अपनी स्वास्थ्य यात्रा जारी रखने के लिए साइन इन करें',
            signupTitle: 'खाता बनाएं 🌿',
            signupSubtitle: 'आज ही अपनी प्राकृतिक स्वास्थ्य यात्रा शुरू करें',
            email: 'ईमेल',
            emailAddress: 'ईमेल पता',
            emailPlaceholder: 'आप@उदाहरण.com',
            password: 'पासवर्ड',
            passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
            createPassword: 'एक मजबूत पासवर्ड बनाएं',
            confirmPassword: 'पासवर्ड की पुष्टि करें',
            confirmPasswordPlaceholder: 'अपना पासवर्ड पुष्टि करें',
            fullName: 'पूरा नाम',
            fullNamePlaceholder: 'जॉन डो',
            rememberMe: 'मुझे याद रखें',
            forgotPassword: 'पासवर्ड भूल गए?',
            noAccount: 'खाता नहीं है?',
            hasAccount: 'पहले से खाता है?',
            signIn: 'साइन इन करें',
            signUp: 'खाता बनाएं',
            signInWith: 'से साइन इन करें',
            signUpWith: 'से साइन अप करें',
            orContinueWith: 'या जारी रखें',
            signUpFree: 'मुफ्त साइन अप करें',
            createAccount: 'अपना मुफ्त खाता बनाएं',
            verifyEmail: 'अपना ईमेल सत्यापित करें',
            otpSent: 'हमने आपके ईमेल पर कोड भेजा है',
            enterOtp: 'OTP दर्ज करें',
            resendOtp: 'OTP पुनः भेजें',
            resetPassword: 'पासवर्ड रीसेट करें',
            sendResetLink: 'रीसेट लिंक भेजें',
            newPassword: 'नया पासवर्ड',
            newPasswordPlaceholder: 'नया पासवर्ड दर्ज करें',
            resetSuccess: 'पासवर्ड रीसेट सफल!',
            backToLogin: 'लॉगिन पर वापस जाएं',
            emailRequired: 'ईमेल आवश्यक है',
            validEmail: 'कृपया वैध ईमेल दर्ज करें',
            passwordRequired: 'पासवर्ड आवश्यक है',
            passwordMin: 'पासवर्ड कम से कम 6 अक्षर का होना चाहिए',
            nameRequired: 'नाम आवश्यक है',
            nameMin: 'नाम कम से कम 2 अक्षर का होना चाहिए',
            confirmPasswordRequired: 'कृपया अपना पासवर्ड पुष्टि करें',
            passwordsMatch: 'पासवर्ड मेल नहीं खाते',
            enterEmailToReset: 'पासवर्ड रीसेट करने के लिए अपना ईमेल दर्ज करें',
            checkEmailForReset: 'रीसेट निर्देशों के लिए अपना ईमेल जांचें',
            otpTitle: 'अपना खाता सत्यापित करें',
            otpSubtitle: 'भेजा गया 6-अंकीय कोड दर्ज करें',
            verifyOtp: 'OTP सत्यापित करें',
            resendIn: 'में पुनः भेजें',
            seconds: 'सेकंड',
            startJourney: 'अपनी स्वास्थ्य यात्रा शुरू करें ✨',
            joinCommunity: 'प्राकृतिक स्वास्थ्य उत्साहियों के समुदाय से जुड़ें',
            atLeast6Chars: 'कम से कम 6 अक्षर',
            oneUppercase: 'एक बड़ा अक्षर',
            oneLowercase: 'एक छोटा अक्षर',
            oneNumber: 'एक अंक',
            termsAgree: 'खाता बनाकर, आप हमारी सहमति देते हैं',
            termsOfService: 'सेवा की शर्तें',
            and: 'और',
            privacyPolicy: 'गोपनीयता नीति',
            loginRequired: 'उपचार बनाने के लिए कृपया लॉग इन करें',
        },
        
        // Errors
        errors: {
            required: 'यह फ़ील्ड आवश्यक है',
            invalidEmail: 'कृपया वैध ईमेल दर्ज करें',
            passwordMin: 'पासवर्ड कम से कम 8 अक्षर का होना चाहिए',
            passwordMatch: 'पासवर्ड मेल नहीं खाते',
            somethingWrong: 'कुछ गलत हो गया',
            tryAgain: 'कृपया पुनः प्रयास करें',
            notFound: 'पेज नहीं मिला',
            unauthorized: 'जारी रखने के लिए कृपया लॉगिन करें',
            forbidden: 'आपके पास अनुमति नहीं है',
        },
        
        // Success messages
        success: {
            remedyCreated: 'उपचार सफलतापूर्वक बनाया गया!',
            remedyUpdated: 'उपचार सफलतापूर्वक अपडेट किया गया!',
            remedyDeleted: 'उपचार सफलतापूर्वक हटाया गया!',
            profileUpdated: 'प्रोफाइल सफलतापूर्वक अपडेट की गई!',
            passwordChanged: 'पासवर्ड सफलतापूर्वक बदला गया!',
            loggedOut: 'सफलतापूर्वक लॉग आउट!',
            welcomeBackUser: 'वापस स्वागत है, {name}!',
            accountCreated: 'खाता सफलतापूर्वक बनाया गया!',
            registrationSuccessful: 'पंजीकरण सफल! कृपया अपना ईमेल सत्यापित करें।',
            pleaseVerifyEmail: 'जारी रखने के लिए कृपया अपना ईमेल सत्यापित करें।',
            emailVerified: 'ईमेल सफलतापूर्वक सत्यापित!',
            otpSentSuccess: 'OTP सफलतापूर्वक भेजा गया!',
            passwordResetOtpSent: 'पासवर्ड रीसेट OTP आपके ईमेल पर भेजा गया।',
            passwordResetSuccess: 'पासवर्ड रीसेट सफल! कृपया लॉगिन करें।',
        },
        
        // Footer
        footer: {
            description: 'आधुनिक स्वास्थ्य के लिए पारंपरिक ज्ञान को संरक्षित करना। पीढ़ियों से चले आ रहे प्राकृतिक घरेलू उपचार खोजें।',
            explore: 'अन्वेषण करें',
            allRemedies: 'सभी उपचार',
            categories: 'श्रेणियाँ',
            popular: 'लोकप्रिय',
            latest: 'नवीनतम',
            categoriesTitle: 'श्रेणियाँ',
            immunityBoosters: 'रोग प्रतिरोधक क्षमता बढ़ाने वाले',
            digestiveHealth: 'पाचन स्वास्थ्य',
            coldAndCough: 'सर्दी और खांसी',
            skinCare: 'त्वचा की देखभाल',
            support: 'सहायता',
            aboutUs: 'हमारे बारे में',
            contact: 'संपर्क करें',
            faq: 'अक्सर पूछे जाने वाले प्रश्न',
            privacyPolicy: 'गोपनीयता नीति',
            copyright: 'सर्वाधिकार सुरक्षित।',
            madeWith: 'बनाया गया',
            forWellness: 'स्वास्थ्य के लिए',
            terms: 'शर्तें',
            privacy: 'गोपनीयता',
            cookies: 'कुकीज़',
        },
        
        // About Page
        about: {
            title: 'हमारे बारे में',
            subtitle: 'होमली की कहानी और पारंपरिक स्वास्थ्य ज्ञान को संरक्षित करने के हमारे मिशन के बारे में जानें।',
            storyTitle: 'हमारी कहानी',
            storyP1: 'होमली एक सरल विश्वास से पैदा हुआ था: हमारे दादा-दादी का ज्ञान संरक्षित और साझा किए जाने योग्य है। सिंथेटिक समाधानों पर निर्भर दुनिया में, हम एक ऐसा स्थान बनाना चाहते थे जहाँ पारंपरिक घरेलू उपचार फल-फूल सकें।',
            storyP2: 'हमारा प्लेटफ़ॉर्म सदियों के प्राकृतिक उपचार ज्ञान को एक साथ लाता है, इसे सभी के लिए सुलभ बनाता है।',
            naturalTitle: '100% प्राकृतिक',
            naturalDesc: 'सभी उपचार प्राकृतिक सामग्री का उपयोग करते हैं जो आपकी रसोई में मिल सकते हैं।',
            communityTitle: 'समुदाय संचालित',
            communityDesc: 'हमारे उपयोगकर्ता उपचार साझा और रेट करते हैं, एक विश्वसनीय ज्ञान आधार बनाते हैं।',
            missionTitle: 'हमारा मिशन',
            missionDesc: 'पारंपरिक स्वास्थ्य ज्ञान को हर जगह सभी के लिए सुलभ बनाना।',
            careTitle: 'देखभाल से बनाया',
            careDesc: 'प्रत्येक उपचार को प्रकाशित करने से पहले सुरक्षा के लिए समीक्षा की जाती है।',
            teamTitle: 'जुनून से बनाया',
            teamDesc: 'होमली भावी पीढ़ियों के लिए हमारे पूर्वजों की उपचार परंपराओं को संरक्षित करने के लिए समर्पित एक जुनून परियोजना है।',
        },
        
        // Contact Page
        contact: {
            title: 'संपर्क करें',
            subtitle: 'कोई प्रश्न या सुझाव है? हम आपसे सुनना पसंद करेंगे!',
            formTitle: 'हमें संदेश भेजें',
            nameLabel: 'आपका नाम',
            namePlaceholder: 'जॉन डो',
            emailLabel: 'ईमेल पता',
            emailPlaceholder: 'you@example.com',
            subjectLabel: 'विषय',
            subjectPlaceholder: 'हम कैसे मदद कर सकते हैं?',
            messageLabel: 'संदेश',
            messagePlaceholder: 'हमें बताएं आपके मन में क्या है...',
            sendButton: 'संदेश भेजें',
            successMessage: 'संदेश सफलतापूर्वक भेजा गया! हम जल्द ही आपसे संपर्क करेंगे।',
            infoTitle: 'संपर्क में रहें',
            email: 'ईमेल',
            location: 'स्थान',
            followUs: 'हमें फॉलो करें',
        },
        
        // FAQ Page
        faq: {
            title: 'अक्सर पूछे जाने वाले प्रश्न',
            subtitle: 'होमली और प्राकृतिक उपचारों के बारे में सामान्य प्रश्नों के उत्तर खोजें।',
            searchPlaceholder: 'प्रश्न खोजें...',
            q1: 'होमली क्या है?',
            a1: 'होमली पारंपरिक घरेलू उपचारों को संरक्षित और साझा करने के लिए समर्पित एक प्लेटफ़ॉर्म है।',
            q2: 'क्या ये उपचार उपयोग करने के लिए सुरक्षित हैं?',
            a2: 'पारंपरिक उपचार आम तौर पर सुरक्षित होते हैं, लेकिन हम हमेशा किसी भी नए उपचार को आजमाने से पहले स्वास्थ्य पेशेवर से परामर्श करने की सलाह देते हैं।',
            q3: 'क्या मैं अपने उपचार योगदान कर सकता हूं?',
            a3: 'हां! एक बार खाता बनाने के बाद, आप अपने परिवार के पारंपरिक उपचार हमारे समुदाय के साथ साझा कर सकते हैं।',
            q4: 'क्या होमली मुफ्त है?',
            a4: 'हां, होमली पूरी तरह से मुफ्त है। आप बिना किसी लागत के उपचार ब्राउज़, खोज और सहेज सकते हैं।',
            q5: 'मैं अपने पसंदीदा उपचार कैसे सहेजूं?',
            a5: 'किसी भी उपचार कार्ड पर बुकमार्क आइकन पर क्लिक करके इसे अपने पसंदीदा में सहेजें।',
            q6: 'AI सहायक क्या है?',
            a6: 'हमारा AI सहायक आपके लक्षणों के आधार पर उपचार खोजने में आपकी मदद कर सकता है।',
            q7: 'मैं किसी उपचार के साथ समस्या की रिपोर्ट कैसे करूं?',
            a7: 'यदि आपको कोई गलत या हानिकारक जानकारी मिलती है, तो कृपया संपर्क फ़ॉर्म का उपयोग करके इसकी रिपोर्ट करें।',
            q8: 'क्या मेरा व्यक्तिगत डेटा सुरक्षित है?',
            a8: 'हां, हम गोपनीयता को गंभीरता से लेते हैं। हम केवल आवश्यक जानकारी एकत्र करते हैं।',
            noResults: 'आपकी खोज से मेल खाने वाले प्रश्न नहीं मिले।',
            stillHaveQuestions: 'अभी भी प्रश्न हैं?',
            contactUs: 'जो आप खोज रहे हैं वह नहीं मिल रहा? हमसे सीधे संपर्क करें।',
            getInTouch: 'संपर्क करें',
        },
        
        // Privacy Page
        privacy: {
            title: 'गोपनीयता नीति',
            subtitle: 'आपकी गोपनीयता हमारे लिए महत्वपूर्ण है। जानें कि हम आपकी जानकारी कैसे एकत्र और उपयोग करते हैं।',
            lastUpdated: 'अंतिम अपडेट',
            intro: 'होमली में, हम आपकी गोपनीयता की रक्षा के लिए प्रतिबद्ध हैं।',
            dataCollectionTitle: 'हम कौन सी जानकारी एकत्र करते हैं',
            dataCollectionContent: 'हम आपके द्वारा सीधे प्रदान की गई जानकारी एकत्र करते हैं, जैसे आपका नाम और ईमेल पता।',
            dataUsageTitle: 'हम आपकी जानकारी का उपयोग कैसे करते हैं',
            dataUsageContent: 'हम अपनी सेवाएं प्रदान करने और सुधारने, आपके अनुभव को व्यक्तिगत बनाने के लिए आपकी जानकारी का उपयोग करते हैं।',
            dataProtectionTitle: 'हम आपके डेटा की सुरक्षा कैसे करते हैं',
            dataProtectionContent: 'हम आपके डेटा की सुरक्षा के लिए उद्योग-मानक सुरक्षा उपाय लागू करते हैं।',
            yourRightsTitle: 'आपके अधिकार',
            yourRightsContent: 'आपको किसी भी समय अपने व्यक्तिगत डेटा तक पहुंचने, सुधारने या हटाने का अधिकार है।',
            questionsTitle: 'प्रश्न?',
            questionsContent: 'यदि आपके पास हमारी गोपनीयता प्रथाओं के बारे में कोई प्रश्न हैं, तो कृपया हमसे संपर्क करें।',
        },
        
        // Terms Page
        terms: {
            title: 'सेवा की शर्तें',
            subtitle: 'होमली का उपयोग करने से पहले कृपया इन शर्तों को ध्यान से पढ़ें।',
            lastUpdated: 'अंतिम अपडेट',
            intro: 'होमली का उपयोग करके, आप इन सेवा की शर्तों से सहमत होते हैं।',
            acceptanceTitle: 'शर्तों की स्वीकृति',
            acceptanceContent: 'होमली तक पहुंचकर या उपयोग करके, आप स्वीकार करते हैं कि आपने इन शर्तों को पढ़ और समझ लिया है।',
            userAccountsTitle: 'उपयोगकर्ता खाते',
            userAccountsContent: 'आप अपने खाता क्रेडेंशियल्स की गोपनीयता बनाए रखने के लिए जिम्मेदार हैं।',
            contentTitle: 'उपयोगकर्ता सामग्री',
            contentContent: 'आप होमली पर बनाई गई सामग्री के स्वामी बने रहते हैं।',
            disclaimerTitle: 'चिकित्सा अस्वीकरण',
            disclaimerContent: 'होमली केवल शैक्षिक उद्देश्यों के लिए जानकारी प्रदान करता है। कृपया किसी भी नए उपचार को आजमाने से पहले स्वास्थ्य सेवा प्रदाता से परामर्श करें।',
            changesTitle: 'शर्तों में परिवर्तन',
            changesContent: 'हम समय-समय पर इन शर्तों को अपडेट कर सकते हैं।',
        },
        
        // Cookies Page
        cookies: {
            title: 'कुकी नीति',
            subtitle: 'जानें कि हम होमली पर आपके अनुभव को बेहतर बनाने के लिए कुकीज़ का उपयोग कैसे करते हैं।',
            whatAreTitle: 'कुकीज़ क्या हैं?',
            whatAreContent: 'कुकीज़ छोटी टेक्स्ट फ़ाइलें हैं जो वेबसाइट पर जाने पर आपके डिवाइस पर संग्रहीत होती हैं।',
            typesTitle: 'हम किस प्रकार की कुकीज़ का उपयोग करते हैं',
            essentialTitle: 'आवश्यक कुकीज़',
            essentialDesc: 'ये कुकीज़ वेबसाइट के ठीक से काम करने के लिए आवश्यक हैं।',
            functionalTitle: 'कार्यात्मक कुकीज़',
            functionalDesc: 'ये कुकीज़ आपकी प्राथमिकताएं याद रखती हैं।',
            analyticsTitle: 'एनालिटिक्स कुकीज़',
            analyticsDesc: 'हम यह समझने के लिए इन कुकीज़ का उपयोग करते हैं कि आगंतुक हमारी वेबसाइट के साथ कैसे इंटरैक्ट करते हैं।',
            required: 'आवश्यक',
            optional: 'वैकल्पिक',
            manageTitle: 'कुकीज़ प्रबंधित करना',
            manageContent: 'आप अपने ब्राउज़र सेटिंग्स के माध्यम से कुकीज़ को नियंत्रित कर सकते हैं।',
            questionsTitle: 'प्रश्न?',
            questionsContent: 'यदि आपके पास हमारी कुकी प्रथाओं के बारे में प्रश्न हैं, तो कृपया हमसे संपर्क करें।',
        },
        
        // Popular Page
        popular: {
            title: 'लोकप्रिय उपचार',
            subtitle: 'हमारे समुदाय में सबसे पसंदीदा उपचार खोजें।',
            remediesFound: 'लोकप्रिय उपचार',
            loginRequired: 'लॉगिन आवश्यक',
            loginToView: 'लोकप्रिय उपचार देखने के लिए कृपया लॉग इन करें।',
            noRemedies: 'कोई लोकप्रिय उपचार नहीं',
            noRemediesDesc: 'ट्रेंडिंग उपचारों के लिए बाद में देखें।',
        },
        
        // Latest Page
        latest: {
            title: 'नवीनतम उपचार',
            subtitle: 'हमारे उपचार संग्रह में नवीनतम जोड़ों का अन्वेषण करें।',
            remediesFound: 'नए उपचार',
            loginRequired: 'लॉगिन आवश्यक',
            loginToView: 'नवीनतम उपचार देखने के लिए कृपया लॉग इन करें।',
            noRemedies: 'कोई नए उपचार नहीं',
            noRemediesDesc: 'एक उपचार साझा करने वाले पहले बनें!',
        },
        
        // 404 Page
        notFound: {
            title: '404',
            subtitle: 'पेज नहीं मिला',
            description: 'उफ़! लगता है यह उपचार बगीचे में खो गया। चलिए आपको परिचित जगह पर वापस ले चलते हैं।',
            backHome: 'होम पर वापस जाएं',
            goBack: 'वापस जाएं',
        },
    },
    
    kn: {
        // Common
        appName: 'ಹೋಮ್ಲಿ',
        tagline: 'ಆಧುನಿಕ ಜೀವನಕ್ಕೆ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು',
        loading: 'ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
        save: 'ಉಳಿಸಿ',
        cancel: 'ರದ್ದುಮಾಡಿ',
        delete: 'ಅಳಿಸಿ',
        edit: 'ಸಂಪಾದಿಸಿ',
        submit: 'ಸಲ್ಲಿಸಿ',
        search: 'ಹುಡುಕಿ',
        close: 'ಮುಚ್ಚಿ',
        back: 'ಹಿಂದೆ',
        next: 'ಮುಂದೆ',
        yes: 'ಹೌದು',
        no: 'ಇಲ್ಲ',
        or: 'ಅಥವಾ',
        and: 'ಮತ್ತು',
        
        // Navigation
        nav: {
            home: 'ಮನೆ',
            dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
            aiAssistant: 'AI ಸಹಾಯಕ',
            createRemedy: 'ಪರಿಹಾರ ರಚಿಸಿ',
            profile: 'ಪ್ರೊಫೈಲ್',
            settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
            login: 'ಲಾಗಿನ್',
            signup: 'ಸೈನ್ ಅಪ್',
            logout: 'ಲಾಗ್ ಔಟ್',
            please: 'ದಯವಿಟ್ಟು',
            or: 'ಅಥವಾ',
        },
        
        // Home Page
        home: {
            heroTitle: 'ನೈಸರ್ಗಿಕ ಶೋಧಿಸಿ',
            heroTitleHighlight: 'ಮನೆ ಮದ್ದುಗಳು',
            heroSubtitle: 'ದೈನಂದಿನ ಆರೋಗ್ಯ ಸಮಸ್ಯೆಗಳಿಗೆ ಕಾಲ-ಪರೀಕ್ಷಿತ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳನ್ನು ಅನ್ವೇಷಿಸಿ. ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಯಾಣ ಇಲ್ಲಿ ಪ್ರಾರಂಭವಾಗುತ್ತದೆ.',
            exploreRemedies: 'ಪರಿಹಾರಗಳನ್ನು ನೋಡಿ',
            askAI: 'AI ಗೆ ಕೇಳಿ',
            searchRemedies: 'ಪರಿಹಾರಗಳನ್ನು ಹುಡುಕಿ',
            featuresTitle: 'ಹೋಮ್ಲಿ ಏಕೆ ಆಯ್ಕೆ ಮಾಡಬೇಕು?',
            feature1Title: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು',
            feature1Desc: 'ನಿಮ್ಮ ಅಡುಗೆಮನೆಯಲ್ಲಿ ಸಿಗುವ ನೈಸರ್ಗಿಕ ಪದಾರ್ಥಗಳನ್ನು ಬಳಸಿ ಪರಿಹಾರಗಳನ್ನು ಕಂಡುಕೊಳ್ಳಿ',
            feature2Title: 'AI-ಚಾಲಿತ ಸಹಾಯ',
            feature2Desc: 'ನಮ್ಮ ಸ್ಮಾರ್ಟ್ AI ಸಹಾಯಕದಿಂದ ವೈಯಕ್ತಿಕ ಪರಿಹಾರ ಸಲಹೆಗಳನ್ನು ಪಡೆಯಿರಿ',
            feature3Title: 'ಸಮುದಾಯ ಚಾಲಿತ',
            feature3Desc: 'ನೈಸರ್ಗಿಕ ಆರೋಗ್ಯ ಉತ್ಸಾಹಿಗಳ ಸಮುದಾಯದಿಂದ ಹಂಚಿಕೊಳ್ಳಿ ಮತ್ತು ಕಲಿಯಿರಿ',
            feature4Title: 'ಸುರಕ್ಷಿತ ಮತ್ತು ಪರಿಶೀಲಿತ',
            feature4Desc: 'ಎಲ್ಲಾ ಪರಿಹಾರಗಳನ್ನು ಸುರಕ್ಷತೆ ಮತ್ತು ಪರಿಣಾಮಕಾರಿತ್ವಕ್ಕಾಗಿ ಪರಿಶೀಲಿಸಲಾಗಿದೆ',
            forEverydayWellness: 'ದೈನಂದಿನ ಆರೋಗ್ಯಕ್ಕಾಗಿ',
            badge: 'ಸಾಂಪ್ರದಾಯಿಕ ಜ್ಞಾನ, ಆಧುನಿಕ ಆರೋಗ್ಯ',
            naturalRemedies: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು',
            categories: 'ವರ್ಗಗಳು',
            happyUsers: 'ಸಂತುಷ್ಟ ಬಳಕೆದಾರರು',
            timeTested: 'ಕಾಲ-ಪರೀಕ್ಷಿತ ಪರಿಹಾರಗಳು',
            timeTestedDesc: 'ತಲೆಮಾರುಗಳ ಸಾಂಪ್ರದಾಯಿಕ ವೈದ್ಯರು ಮತ್ತು ಆಯುರ್ವೇದ ತಜ್ಞರಿಂದ ಪರಿಶೀಲಿಸಿದ ಶತಮಾನಗಳ ಹಳೆಯ ಪಾಕವಿಧಾನಗಳನ್ನು ಪ್ರವೇಶಿಸಿ.',
            simpleIngredients: 'ಸರಳ ಪದಾರ್ಥಗಳು',
            simpleIngredientsDesc: 'ಅರಿಶಿನ, ಶುಂಠಿ ಮತ್ತು ಜೇನುತುಪ್ಪದಂತಹ ದೈನಂದಿನ ಅಡುಗೆಮನೆ ಪದಾರ್ಥಗಳನ್ನು ಬಳಸಿ ಶಕ್ತಿಶಾಲಿ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳನ್ನು ರಚಿಸಿ.',
            safeNatural: 'ಸುರಕ್ಷಿತ ಮತ್ತು ನೈಸರ್ಗಿಕ',
            safeNaturalDesc: 'ಹಾನಿಕಾರಕ ರಾಸಾಯನಿಕಗಳಿಲ್ಲದ ಸೌಮ್ಯ ಪರಿಹಾರಗಳು. ಪ್ರತಿ ಪರಿಹಾರವು ಸುರಕ್ಷತಾ ಮಾರ್ಗಸೂಚಿಗಳು ಮತ್ತು ಮುನ್ನೆಚ್ಚರಿಕೆಗಳನ್ನು ಒಳಗೊಂಡಿದೆ.',
            aiAssistant: 'AI ಆರೋಗ್ಯ ಸಹಾಯಕ',
            aiAssistantDesc: 'ಸಾಂಪ್ರದಾಯಿಕ ಔಷಧ ಜ್ಞಾನದ ಮೇಲೆ ತರಬೇತಿ ಪಡೆದ ನಮ್ಮ ಬುದ್ಧಿವಂತ ಸಹಾಯಕದಿಂದ ವೈಯಕ್ತಿಕ ಮಾರ್ಗದರ್ಶನ ಪಡೆಯಿರಿ.',
            detailedInstructions: 'ವಿಸ್ತೃತ ಸೂಚನೆಗಳು',
            detailedInstructionsDesc: 'ಪದಾರ್ಥ ಪಟ್ಟಿಗಳು, ಸಮಯ ಮತ್ತು ಡೋಸ್ ಶಿಫಾರಸುಗಳೊಂದಿಗೆ ಹಂತ-ಹಂತದ ತಯಾರಿ ಮಾರ್ಗದರ್ಶಿಗಳು.',
            communityWisdom: 'ಸಮುದಾಯ ಜ್ಞಾನ',
            communityWisdomDesc: 'ತಮ್ಮ ಅನುಭವಗಳು ಮತ್ತು ಸಾಂಪ್ರದಾಯಿಕ ಜ್ಞಾನವನ್ನು ಹಂಚಿಕೊಳ್ಳುತ್ತಿರುವ ಸಾವಿರಾರು ಜನರೊಂದಿಗೆ ಸೇರಿ.',
            howItWorks: 'ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ',
            step1Title: 'ಪರಿಹಾರಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಿ',
            step1Desc: 'ಸಾಮಾನ್ಯ ಕಾಯಿಲೆಗಳಿಗೆ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳ ನಮ್ಮ ಸಂಗ್ರಹವನ್ನು ಅನ್ವೇಷಿಸಿ.',
            step2Title: 'ಪದಾರ್ಥಗಳನ್ನು ಪಡೆಯಿರಿ',
            step2Desc: 'ಹೆಚ್ಚಿನ ಪದಾರ್ಥಗಳು ಈಗಾಗಲೇ ನಿಮ್ಮ ಅಡುಗೆಮನೆಯಲ್ಲಿವೆ ಅಥವಾ ಸುಲಭವಾಗಿ ಲಭ್ಯವಿದೆ.',
            step3Title: 'ಸೂಚನೆಗಳನ್ನು ಅನುಸರಿಸಿ',
            step3Desc: 'ಸರಳ ಹಂತ-ಹಂತದ ಮಾರ್ಗದರ್ಶಿಗಳು ತಯಾರಿಯನ್ನು ಸುಲಭಗೊಳಿಸುತ್ತವೆ.',
            step4Title: 'ಉತ್ತಮವಾಗಿ ಅನುಭವಿಸಿ',
            step4Desc: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳ ಗುಣಪಡಿಸುವ ಶಕ್ತಿಯನ್ನು ಅನುಭವಿಸಿ.',
            testimonials: 'ನಮ್ಮ ಬಳಕೆದಾರರು ಏನು ಹೇಳುತ್ತಾರೆ',
            testimonialsSubtitle: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳ ಶಕ್ತಿಯನ್ನು ಕಂಡುಕೊಂಡ ಸಾವಿರಾರು ಜನರೊಂದಿಗೆ ಸೇರಿ',
            communityStories: 'ಸಮುದಾಯ ಕಥೆಗಳು',
            whatCommunitySays: 'ನಮ್ಮ ಸಮುದಾಯ ಏನು ಹೇಳುತ್ತದೆ',
            realExperiences: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳನ್ನು ಬಳಸುವ ನಿಜವಾದ ಜನರ ನಿಜವಾದ ಅನುಭವಗಳು',
            beFirstToShare: 'ನಿಮ್ಮ ಅನುಭವವನ್ನು ಹಂಚಿಕೊಳ್ಳುವ ಮೊದಲ ವ್ಯಕ್ತಿಯಾಗಿ!',
            communityGettingStarted: 'ನಮ್ಮ ಸಮುದಾಯ ಈಗಷ್ಟೇ ಪ್ರಾರಂಭವಾಗುತ್ತಿದೆ. ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಿವೆ ಎಂಬುದನ್ನು ಹಂಚಿಕೊಳ್ಳಿ ಮತ್ತು ಇತರರನ್ನು ಅವರ ಆರೋಗ್ಯ ಪ್ರಯಾಣದಲ್ಲಿ ಪ್ರೇರೇಪಿಸಿ.',
            writeTestimonial: 'ಸಾಕ್ಷ್ಯ ಬರೆಯಿರಿ',
            loginToShare: 'ನಿಮ್ಮ ಕಥೆ ಹಂಚಿಕೊಳ್ಳಲು ಲಾಗಿನ್ ಮಾಡಿ',
            shareYourExperience: 'ನಿಮ್ಮ ಅನುಭವ ಹಂಚಿಕೊಳ್ಳಿ',
            updateYourTestimonial: 'ನಿಮ್ಮ ಸಾಕ್ಷ್ಯ ಅಪ್ಡೇಟ್ ಮಾಡಿ',
            yourRating: 'ನಿಮ್ಮ ರೇಟಿಂಗ್',
            yourTestimonial: 'ನಿಮ್ಮ ಸಾಕ್ಷ್ಯ',
            testimonialPlaceholder: 'ಹೋಮ್ಲಿ ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಯಾಣದಲ್ಲಿ ಹೇಗೆ ಸಹಾಯ ಮಾಡಿದೆ ಎಂಬುದನ್ನು ಹಂಚಿಕೊಳ್ಳಿ...',
            characters: 'ಅಕ್ಷರಗಳು',
            submitting: 'ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ...',
            submitTestimonial: 'ಸಾಕ್ಷ್ಯ ಸಲ್ಲಿಸಿ',
            updateTestimonial: 'ಸಾಕ್ಷ್ಯ ಅಪ್ಡೇಟ್ ಮಾಡಿ',
            testimonialUpdated: 'ಸಾಕ್ಷ್ಯ ಯಶಸ್ವಿಯಾಗಿ ಅಪ್ಡೇಟ್ ಆಗಿದೆ!',
            thankYouTestimonial: 'ನಿಮ್ಮ ಸಾಕ್ಷ್ಯಕ್ಕೆ ಧನ್ಯವಾದಗಳು!',
            editMyTestimonial: 'ನನ್ನ ಸಾಕ್ಷ್ಯ ಸಂಪಾದಿಸಿ',
            shareYourStory: 'ನಿಮ್ಮ ಕಥೆ ಹಂಚಿಕೊಳ್ಳಿ',
            ctaTitle: 'ನಿಮ್ಮ ನೈಸರ್ಗಿಕ ಆರೋಗ್ಯ ಪ್ರಯಾಣವನ್ನು ಪ್ರಾರಂಭಿಸಿ',
            ctaSubtitle: 'ತಮ್ಮ ಆರೋಗ್ಯ ಅಗತ್ಯಗಳಿಗೆ ಹೋಮ್ಲಿಯನ್ನು ನಂಬುವ ಸಾವಿರಾರು ಬಳಕೆದಾರರೊಂದಿಗೆ ಸೇರಿ.',
            getStartedFree: 'ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭಿಸಿ',
            learnMore: 'ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ',
            popularCategories: 'ಜನಪ್ರಿಯ ವರ್ಗಗಳು',
            viewAll: 'ಎಲ್ಲಾ ನೋಡಿ',
            readyToStart: 'ಪ್ರಾರಂಭಿಸಲು ಸಿದ್ಧರಿದ್ದೀರಾ?',
            joinCommunity: 'ನೈಸರ್ಗಿಕ ಆರೋಗ್ಯ ಉತ್ಸಾಹಿಗಳ ನಮ್ಮ ಸಮುದಾಯಕ್ಕೆ ಸೇರಿ',
            signUpFree: 'ಉಚಿತ ಸೈನ್ ಅಪ್',
            freeForever: 'ಶಾಶ್ವತವಾಗಿ ಉಚಿತ',
            safe: '100% ಸುರಕ್ಷಿತ',
            noAds: 'ಯಾವುದೇ ಜಾಹೀರಾತುಗಳಿಲ್ಲ',
            alreadyMember: 'ಈಗಾಗಲೇ ಸದಸ್ಯರೇ?',
            signIn: 'ಸೈನ್ ಇನ್ ಮಾಡಿ',
        },
        
        // Dashboard
        dashboard: {
            title: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳನ್ನು ಶೋಧಿಸಿ',
            subtitle: 'ವಿವಿಧ ಆರೋಗ್ಯ ಸಮಸ್ಯೆಗಳಿಗೆ ಕಾಲ-ಪರೀಕ್ಷಿತ ಮನೆ ಮದ್ದುಗಳ ಸಂಗ್ರಹವನ್ನು ನೋಡಿ',
            searchPlaceholder: 'ಹೆಸರು, ಪದಾರ್ಥ, ಅಥವಾ ಸ್ಥಿತಿಯಿಂದ ಪರಿಹಾರಗಳನ್ನು ಹುಡುಕಿ...',
            voiceSearch: 'ಧ್ವನಿ ಹುಡುಕಾಟ',
            addRemedy: 'ಪರಿಹಾರ ಸೇರಿಸಿ',
            noResults: 'ಯಾವುದೇ ಪರಿಹಾರಗಳು ಕಂಡುಬಂದಿಲ್ಲ',
            noResultsDesc: 'ನಿಮ್ಮ ಹುಡುಕಾಟ ಅಥವಾ ಫಿಲ್ಟರ್‌ಗಳನ್ನು ಬದಲಾಯಿಸಲು ಪ್ರಯತ್ನಿಸಿ',
            clearFilters: 'ಫಿಲ್ಟರ್‌ಗಳನ್ನು ತೆರವುಗೊಳಿಸಿ',
            sortBy: 'ವಿಂಗಡಿಸಿ',
            newest: 'ಹೊಸದು ಮೊದಲು',
            oldest: 'ಹಳೆಯದು ಮೊದಲು',
            popular: 'ಅತ್ಯಂತ ಜನಪ್ರಿಯ',
            highestRated: 'ಅತ್ಯುತ್ತಮ ರೇಟಿಂಗ್',
            alphabetical: 'A-Z',
            remediesFound: 'ಪರಿಹಾರಗಳು ಕಂಡುಬಂದಿವೆ',
            loadMore: 'ಇನ್ನಷ್ಟು ಪರಿಹಾರಗಳನ್ನು ಲೋಡ್ ಮಾಡಿ',
            showing: 'ತೋರಿಸುತ್ತಿದೆ',
            of: 'ರಲ್ಲಿ',
            guestBanner: 'ನೀವು ಮಾದರಿ ಪರಿಹಾರಗಳನ್ನು ನೋಡುತ್ತಿದ್ದೀರಿ. ಎಲ್ಲಾ ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ಅನ್ಲಾಕ್ ಮಾಡಲು ಸೈನ್ ಇನ್ ಮಾಡಿ!',
            signInNow: 'ಈಗ ಸೈನ್ ಇನ್ ಮಾಡಿ',
            loginRequired: 'ಲಾಗಿನ್ ಅಗತ್ಯವಿದೆ',
            loginRequiredDesc: 'ಪೂರ್ಣ ಪರಿಹಾರ ವಿವರಗಳನ್ನು ನೋಡಲು, ಮೆಚ್ಚಿನವುಗಳನ್ನು ಉಳಿಸಲು ಮತ್ತು ಎಲ್ಲಾ ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ಪ್ರವೇಶಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ!',
            loginToSearch: 'ಪರಿಹಾರಗಳನ್ನು ಹುಡುಕಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            loginToFilter: 'ವರ್ಗದ ಮೂಲಕ ಫಿಲ್ಟರ್ ಮಾಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            loginToSort: 'ವಿಂಗಡಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            loginToSave: 'ಪರಿಹಾರಗಳನ್ನು ಉಳಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            remedySaved: 'ಪರಿಹಾರ ಉಳಿಸಲಾಗಿದೆ!',
            maybeLater: 'ನಂತರ',
        },
        
        // Categories
        categories: {
            all: 'ಎಲ್ಲಾ',
            immunityBooster: 'ರೋಗನಿರೋಧಕ',
            digestiveHealth: 'ಜೀರ್ಣಕ್ರಿಯೆ',
            respiratoryHealth: 'ಶ್ವಾಸಕೋಶ',
            skinCare: 'ಚರ್ಮ ಆರೈಕೆ',
            hairCare: 'ಕೂದಲು ಆರೈಕೆ',
            painRelief: 'ನೋವು ನಿವಾರಣೆ',
            stressRelief: 'ಒತ್ತಡ ನಿವಾರಣೆ',
            sleepAid: 'ನಿದ್ರೆ ಸಹಾಯ',
            energyBoost: 'ಶಕ್ತಿ',
            detox: 'ಡಿಟಾಕ್ಸ್',
            weightManagement: 'ತೂಕ',
            heartHealth: 'ಹೃದಯ ಆರೋಗ್ಯ',
            boneJointHealth: 'ಮೂಳೆ ಮತ್ತು ಕೀಲು',
            eyeHealth: 'ಕಣ್ಣು ಆರೋಗ್ಯ',
            oralHealth: 'ಬಾಯಿ ಆರೋಗ್ಯ',
            womensHealth: 'ಮಹಿಳಾ ಆರೋಗ್ಯ',
            mensHealth: 'ಪುರುಷರ ಆರೋಗ್ಯ',
            childHealth: 'ಮಕ್ಕಳ ಆರೋಗ್ಯ',
            elderCare: 'ಹಿರಿಯರ ಆರೈಕೆ',
            other: 'ಇತರೆ',
            // Categories Page
            title: 'ವರ್ಗಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಿ',
            subtitle: 'ಆರೋಗ್ಯ ಕಾಳಜಿಯ ಪ್ರಕಾರ ಸಂಘಟಿತ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳ ನಮ್ಮ ಸಂಗ್ರಹವನ್ನು ಅನ್ವೇಷಿಸಿ.',
            immunityBoosterDesc: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳೊಂದಿಗೆ ನಿಮ್ಮ ರೋಗನಿರೋಧಕ ವ್ಯವಸ್ಥೆಯನ್ನು ಬಲಪಡಿಸಿ.',
            digestiveHealthDesc: 'ಉತ್ತಮ ಜೀರ್ಣಕ್ರಿಯೆಗೆ ಪರಿಹಾರಗಳು.',
            coldAndCough: 'ಶೀತ ಮತ್ತು ಕೆಮ್ಮು',
            coldAndCoughDesc: 'ಶೀತ ಮತ್ತು ಕೆಮ್ಮು ರೋಗಲಕ್ಷಣಗಳಿಂದ ನೈಸರ್ಗಿಕ ಪರಿಹಾರ.',
            skinCareDesc: 'ಆರೋಗ್ಯಕರ, ಹೊಳೆಯುವ ಚರ್ಮಕ್ಕೆ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು.',
            hairCareDesc: 'ಬಲವಾದ, ಆರೋಗ್ಯಕರ ಕೂದಲಿಗೆ ಸಾಂಪ್ರದಾಯಿಕ ಚಿಕಿತ್ಸೆಗಳು.',
            stressReliefDesc: 'ಒತ್ತಡ ಮತ್ತು ಆತಂಕವನ್ನು ಕಡಿಮೆ ಮಾಡಲು ನೈಸರ್ಗಿಕ ಮಾರ್ಗಗಳು.',
            energyVitality: 'ಶಕ್ತಿ ಮತ್ತು ಚೈತನ್ಯ',
            energyVitalityDesc: 'ನಿಮ್ಮ ಶಕ್ತಿ ಮಟ್ಟವನ್ನು ನೈಸರ್ಗಿಕವಾಗಿ ಹೆಚ್ಚಿಸಿ.',
            generalWellness: 'ಸಾಮಾನ್ಯ ಆರೋಗ್ಯ',
            generalWellnessDesc: 'ಒಟ್ಟಾರೆ ಆರೋಗ್ಯ ಮತ್ತು ಕಲ್ಯಾಣ ಪರಿಹಾರಗಳು.',
            explore: 'ಅನ್ವೇಷಿಸಿ',
            backToCategories: 'ವರ್ಗಗಳಿಗೆ ಹಿಂತಿರುಗಿ',
            remediesFound: 'ಪರಿಹಾರಗಳು ಕಂಡುಬಂದಿವೆ',
            loginRequired: 'ಲಾಗಿನ್ ಅಗತ್ಯವಿದೆ',
            loginToView: 'ಈ ವರ್ಗದಲ್ಲಿನ ಪರಿಹಾರಗಳನ್ನು ನೋಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ.',
            noRemedies: 'ಇನ್ನೂ ಯಾವುದೇ ಪರಿಹಾರಗಳಿಲ್ಲ',
            noRemediesDesc: 'ಈ ವರ್ಗದಲ್ಲಿ ಮೊದಲ ಪರಿಹಾರವನ್ನು ಸೇರಿಸಿ!',
            createFirst: 'ಮೊದಲ ಪರಿಹಾರ ರಚಿಸಿ',
        },
        
        // Remedy Detail
        remedy: {
            ingredients: 'ಪದಾರ್ಥಗಳು',
            instructions: 'ಸೂಚನೆಗಳು',
            reviews: 'ವಿಮರ್ಶೆಗಳು',
            preparation: 'ತಯಾರಿ',
            benefits: 'ಪ್ರಯೋಜನಗಳು',
            precautions: 'ಮುನ್ನೆಚ್ಚರಿಕೆಗಳು',
            warnings: 'ಎಚ್ಚರಿಕೆಗಳು',
            dosage: 'ಡೋಸ್',
            bestTime: 'ಬಳಸಲು ಉತ್ತಮ ಸಮಯ',
            difficulty: 'ಕಷ್ಟದ ಮಟ್ಟ',
            prepTime: 'ತಯಾರಿ ಸಮಯ',
            servings: 'ಬಡಿಸುವಿಕೆ',
            minutes: 'ನಿಮಿಷಗಳು',
            easy: 'ಸುಲಭ',
            medium: 'ಮಧ್ಯಮ',
            hard: 'ಕಷ್ಟ',
            shareRemedy: 'ಹಂಚಿಕೊಳ್ಳಿ',
            saveRemedy: 'ಪರಿಹಾರ ಉಳಿಸಿ',
            print: 'ಮುದ್ರಿಸಿ',
            like: 'ಇಷ್ಟ',
            liked: 'ಇಷ್ಟಪಟ್ಟಿದ್ದೀರಿ',
            reportRemedy: 'ವರದಿ ಮಾಡಿ',
            createdBy: 'ರಚಿಸಿದವರು',
            sharedBy: 'ಹಂಚಿಕೊಂಡವರು',
            communityMember: 'ಸಮುದಾಯ ಸದಸ್ಯ',
            askAboutRemedy: 'ಈ ಪರಿಹಾರದ ಬಗ್ಗೆ AI ಗೆ ಕೇಳಿ',
            notFound: 'ಪರಿಹಾರ ಕಂಡುಬಂದಿಲ್ಲ',
            notFoundDesc: 'ನೀವು ಹುಡುಕುತ್ತಿರುವ ಪರಿಹಾರ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ.',
            backToDashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಹಿಂತಿರುಗಿ',
            loadError: 'ಪರಿಹಾರ ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
            savedToCollection: 'ಸಂಗ್ರಹಕ್ಕೆ ಉಳಿಸಲಾಗಿದೆ!',
            removedFromSaved: 'ಉಳಿಸಿದವುಗಳಿಂದ ತೆಗೆದುಹಾಕಲಾಗಿದೆ',
            saveError: 'ಪರಿಹಾರ ಉಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
            loginToLike: 'ಇಷ್ಟಪಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            loginToRate: 'ರೇಟ್ ಮಾಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            thanksForRating: 'ರೇಟಿಂಗ್‌ಗಾಗಿ ಧನ್ಯವಾದಗಳು!',
            ratingError: 'ರೇಟಿಂಗ್ ಸಲ್ಲಿಸಲು ವಿಫಲವಾಗಿದೆ',
            linkCopied: 'ಲಿಂಕ್ ಕ್ಲಿಪ್‌ಬೋರ್ಡ್‌ಗೆ ನಕಲಿಸಲಾಗಿದೆ!',
            rateThisRemedy: 'ಈ ಪರಿಹಾರವನ್ನು ರೇಟ್ ಮಾಡಿ',
            youRated: 'ನೀವು ರೇಟ್ ಮಾಡಿದ್ದೀರಿ',
            stars: 'ನಕ್ಷತ್ರಗಳು',
            clickToRate: 'ರೇಟ್ ಮಾಡಲು ಕ್ಲಿಕ್ ಮಾಡಿ',
            noReviews: 'ಇನ್ನೂ ಯಾವುದೇ ವಿಮರ್ಶೆಗಳಿಲ್ಲ.',
            beFirstToReview: 'ನಿಮ್ಮ ಅನುಭವವನ್ನು ಹಂಚಿಕೊಳ್ಳುವ ಮೊದಲಿಗರಾಗಿ!',
            anonymous: 'ಅನಾಮಧೇಯ',
        },
        
        // Create Remedy
        create: {
            title: 'ಹೊಸ ಪರಿಹಾರ ರಚಿಸಿ',
            editTitle: 'ಪರಿಹಾರ ಸಂಪಾದಿಸಿ',
            basicInfo: 'ಮೂಲ ಮಾಹಿತಿ',
            remedyTitle: 'ಶೀರ್ಷಿಕೆ',
            titlePlaceholder: 'ಉದಾ., ಶೀತಕ್ಕೆ ಶುಂಠಿ ಜೇನು ಚಹಾ',
            description: 'ವಿವರಣೆ',
            descPlaceholder: 'ಪರಿಹಾರದ ಸಂಕ್ಷಿಪ್ತ ವಿವರಣೆ...',
            category: 'ವರ್ಗ',
            selectCategory: 'ವರ್ಗ ಆಯ್ಕೆಮಾಡಿ',
            addIngredient: 'ಪದಾರ್ಥ ಸೇರಿಸಿ',
            addStep: 'ಹಂತ ಸೇರಿಸಿ',
            ingredientName: 'ಪದಾರ್ಥ',
            quantity: 'ಪ್ರಮಾಣ',
            unit: 'ಘಟಕ',
            optional: 'ಐಚ್ಛಿಕ',
            stepInstruction: 'ಸೂಚನೆ',
            duration: 'ಅವಧಿ',
            tips: 'ಸಲಹೆಗಳು',
            publishRemedy: 'ಪರಿಹಾರ ಪ್ರಕಟಿಸಿ',
            saveDraft: 'ಡ್ರಾಫ್ಟ್ ಉಳಿಸಿ',
            aiAssist: 'AI ಸಹಾಯ',
        },
        
        // Create Remedy Page (detailed)
        createRemedy: {
            title: 'ಹೊಸ ಪರಿಹಾರ ರಚಿಸಿ',
            subtitle: 'ಸಮುದಾಯದೊಂದಿಗೆ ನಿಮ್ಮ ನೈಸರ್ಗಿಕ ಪರಿಹಾರವನ್ನು ಹಂಚಿಕೊಳ್ಳಿ',
            basicInfo: 'ಮೂಲ ಮಾಹಿತಿ',
            name: 'ಪರಿಹಾರದ ಹೆಸರು',
            namePlaceholder: 'ಉದಾ., ಶೀತಕ್ಕೆ ಜೇನು ಶುಂಠಿ ಚಹಾ',
            description: 'ವಿವರಣೆ',
            descriptionPlaceholder: 'ನಿಮ್ಮ ಪರಿಹಾರ ಮತ್ತು ಅದರ ಪ್ರಯೋಜನಗಳನ್ನು ವಿವರಿಸಿ...',
            category: 'ವರ್ಗ',
            difficulty: 'ಕಷ್ಟದ ಮಟ್ಟ',
            preparationTime: 'ತಯಾರಿ ಸಮಯ (ನಿಮಿಷಗಳು)',
            optional: 'ಐಚ್ಛಿಕ',
            ingredients: 'ಪದಾರ್ಥಗಳು',
            addIngredient: 'ಪದಾರ್ಥ ಸೇರಿಸಿ',
            ingredientName: 'ಪದಾರ್ಥದ ಹೆಸರು',
            amount: 'ಪ್ರಮಾಣ',
            unit: 'ಘಟಕ',
            steps: 'ಸೂಚನೆಗಳು',
            stepInstruction: 'ಹಂತ-ಹಂತದ ಸೂಚನೆಗಳನ್ನು ಸೇರಿಸಿ',
            addStep: 'ಹಂತ ಸೇರಿಸಿ',
            stepPlaceholder: 'ಈ ಹಂತವನ್ನು ವಿವರಿಸಿ...',
            tips: 'ಹೆಚ್ಚುವರಿ ಮಾಹಿತಿ',
            benefits: 'ಪ್ರಯೋಜನಗಳು',
            addBenefit: 'ಪ್ರಯೋಜನ ಸೇರಿಸಿ',
            benefitPlaceholder: 'ಉದಾ., ರೋಗನಿರೋಧಕ ಶಕ್ತಿ ಹೆಚ್ಚಿಸುತ್ತದೆ',
            warnings: 'ಎಚ್ಚರಿಕೆಗಳು ಮತ್ತು ಮುನ್ನೆಚ್ಚರಿಕೆಗಳು',
            addWarning: 'ಎಚ್ಚರಿಕೆ ಸೇರಿಸಿ',
            warningPlaceholder: 'ಉದಾ., ಗರ್ಭಾವಸ್ಥೆಯಲ್ಲಿ ಶಿಫಾರಸು ಮಾಡಲಾಗುವುದಿಲ್ಲ',
            tags: 'ಟ್ಯಾಗ್‌ಗಳು',
            addTag: 'ಟ್ಯಾಗ್ ಸೇರಿಸಿ',
            tagPlaceholder: 'ಉದಾ., ರೋಗನಿರೋಧಕ, ಶೀತ, ನೈಸರ್ಗಿಕ',
            imageUrl: 'ಚಿತ್ರ URL',
            publishRemedy: 'ಪರಿಹಾರ ಪ್ರಕಟಿಸಿ',
            publishing: 'ಪ್ರಕಟಿಸಲಾಗುತ್ತಿದೆ...',
            saveDraft: 'ಡ್ರಾಫ್ಟ್ ಉಳಿಸಿ',
            next: 'ಮುಂದೆ',
            previous: 'ಹಿಂದೆ',
            step: 'ಹಂತ',
            of: 'ರಲ್ಲಿ',
            reviewSubmit: 'ಪರಿಶೀಲಿಸಿ ಮತ್ತು ಸಲ್ಲಿಸಿ',
            reviewInfo: 'ಪ್ರಕಟಿಸುವ ಮೊದಲು ನಿಮ್ಮ ಪರಿಹಾರವನ್ನು ಪರಿಶೀಲಿಸಿ',
            remedySummary: 'ಪರಿಹಾರ ಸಾರಾಂಶ',
            summaryTitle: 'ಶೀರ್ಷಿಕೆ',
            items: 'ಐಟಂಗಳು',
            stepsCount: 'ಹಂತಗಳು',
            success: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ರಚಿಸಲಾಗಿದೆ!',
            error: 'ಪರಿಹಾರ ರಚಿಸಲು ವಿಫಲವಾಗಿದೆ',
            categoryDigestive: 'ಜೀರ್ಣಕ್ರಿಯೆ ಆರೋಗ್ಯ',
            categoryRespiratory: 'ಉಸಿರಾಟ',
            categorySkin: 'ಚರ್ಮ ಆರೈಕೆ',
            categoryImmunity: 'ರೋಗನಿರೋಧಕ ಶಕ್ತಿ',
            categorySleep: 'ನಿದ್ರೆ ಮತ್ತು ವಿಶ್ರಾಂತಿ',
            categoryPain: 'ನೋವು ನಿವಾರಣೆ',
            categoryHair: 'ಕೂದಲು ಆರೈಕೆ',
            categoryEnergy: 'ಶಕ್ತಿ ಮತ್ತು ಚೈತನ್ಯ',
            categoryStress: 'ಒತ್ತಡ ನಿವಾರಣೆ',
            categoryOther: 'ಇತರೆ',
            difficultyEasy: 'ಸುಲಭ',
            difficultyEasyDesc: 'ಸರಳ ಪದಾರ್ಥಗಳು, ತ್ವರಿತ ತಯಾರಿ',
            difficultyMedium: 'ಮಧ್ಯಮ',
            difficultyMediumDesc: 'ಸ್ವಲ್ಪ ತಯಾರಿ ಅಗತ್ಯ',
            difficultyHard: 'ಕಷ್ಟ',
            difficultyHardDesc: 'ಸಂಕೀರ್ಣ ಪ್ರಕ್ರಿಯೆ ಅಥವಾ ಅಪರೂಪದ ಪದಾರ್ಥಗಳು',
            // Edit mode
            editTitle: 'ಪರಿಹಾರ ಸಂಪಾದಿಸಿ',
            editSubtitle: 'ನಿಮ್ಮ ಪರಿಹಾರದ ವಿವರಗಳನ್ನು ನವೀಕರಿಸಿ',
            updateRemedy: 'ಪರಿಹಾರ ನವೀಕರಿಸಿ',
            updating: 'ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ...',
            updateSuccess: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ!',
            updateError: 'ಪರಿಹಾರ ನವೀಕರಿಸಲು ವಿಫಲವಾಗಿದೆ',
            // Delete
            deleteRemedy: 'ಪರಿಹಾರ ಅಳಿಸಿ',
            deleteConfirmTitle: 'ಈ ಪರಿಹಾರವನ್ನು ಅಳಿಸುವುದೇ?',
            deleteConfirmMessage: 'ಈ ಕ್ರಿಯೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗುವುದಿಲ್ಲ. ನೀವು ಖಚಿತವಾಗಿ ಈ ಪರಿಹಾರವನ್ನು ಅಳಿಸಲು ಬಯಸುವಿರಾ?',
            deleteSuccess: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ',
            deleteError: 'ಪರಿಹಾರ ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
            cancel: 'ರದ್ದುಮಾಡಿ',
            confirm: 'ದೃಢೀಕರಿಸಿ',
            // Validation
            descriptionMinLength: 'ವಿವರಣೆ ಕನಿಷ್ಠ 20 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
        },
        
        // Dashboard Categories
        dashboardCategories: {
            all: 'ಎಲ್ಲಾ ಪರಿಹಾರಗಳು',
            digestive: 'ಜೀರ್ಣಕ್ರಿಯೆ',
            respiratory: 'ಉಸಿರಾಟ',
            skinCare: 'ಚರ್ಮ ಆರೈಕೆ',
            immunity: 'ರೋಗನಿರೋಧಕ ಶಕ್ತಿ',
            stressRelief: 'ಒತ್ತಡ ನಿವಾರಣೆ',
            sleep: 'ನಿದ್ರೆ',
            hairCare: 'ಕೂದಲು ಆರೈಕೆ',
            painRelief: 'ನೋವು ನಿವಾರಣೆ',
            energyBooster: 'ಶಕ್ತಿ ಬೂಸ್ಟರ್',
        },
        
        // Remedy Detail Page extras
        remedyDetail: {
            noInstructions: 'ಯಾವುದೇ ಸೂಚನೆಗಳು ಲಭ್ಯವಿಲ್ಲ',
            noIngredients: 'ಯಾವುದೇ ಪದಾರ್ಥಗಳು ಪಟ್ಟಿ ಮಾಡಲಾಗಿಲ್ಲ',
            noBenefits: 'ಯಾವುದೇ ಪ್ರಯೋಜನಗಳು ಪಟ್ಟಿ ಮಾಡಲಾಗಿಲ್ಲ',
            noPrecautions: 'ಯಾವುದೇ ಮುನ್ನೆಚ್ಚರಿಕೆಗಳು ಪಟ್ಟಿ ಮಾಡಲಾಗಿಲ್ಲ',
            preparationSteps: 'ತಯಾರಿ ಹಂತಗಳು',
            instructions: 'ಸೂಚನೆಗಳು',
            resetProgress: 'ಪ್ರಗತಿ ಮರುಹೊಂದಿಸಿ',
            allDone: 'ಎಲ್ಲಾ ಮುಗಿಯಿತು!',
            remedyReady: 'ನಿಮ್ಮ ಪರಿಹಾರ ಸಿದ್ಧವಾಗಿದೆ. ನೈಸರ್ಗಿಕ ಚಿಕಿತ್ಸೆಯ ಪ್ರಯೋಜನಗಳನ್ನು ಆನಂದಿಸಿ!',
            tip: 'ಸಲಹೆ',
            stepNumber: 'ಹಂತ',
            minutesShort: 'ನಿಮಿ',
            servings: 'ಪರಿಮಾಣ',
            bestTime: 'ಉತ್ತಮ ಸಮಯ',
            origin: 'ಮೂಲ',
            suitable: 'ಸೂಕ್ತ',
            notSuitable: 'ಸೂಕ್ತವಲ್ಲ',
            adults: 'ವಯಸ್ಕರು',
            children: 'ಮಕ್ಕಳು',
            elderly: 'ಹಿರಿಯರು',
            pregnant: 'ಗರ್ಭಿಣಿಯರು',
            nursing: 'ಹಾಲುಣಿಸುವ ತಾಯಂದಿರು',
        },
        
        // AI Assistant
        ai: {
            title: 'AI ಸಹಾಯಕ',
            pageTitle: 'AI ಆರೋಗ್ಯ ಸಹಾಯಕ',
            subtitle: 'ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ನೈಸರ್ಗಿಕ ಪರಿಹಾರ ಮಾರ್ಗದರ್ಶಿ',
            poweredBy: 'AI ಮೂಲಕ ಚಾಲಿತ • ನೈಸರ್ಗಿಕ ಪರಿಹಾರ ತಜ್ಞ',
            naturalRemediesExpert: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರ ತಜ್ಞ',
            placeholder: 'ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು, ಆರೋಗ್ಯ ಸಲಹೆಗಳು, ಅಥವಾ ಕ್ಷೇಮ ಸಲಹೆ ಬಗ್ಗೆ ಕೇಳಿ...',
            askMeAnything: 'ನನ್ನನ್ನು ಏನಾದರೂ ಕೇಳಿ...',
            loginToChat: 'ಚಾಟ್ ಮಾಡಲು ಲಾಗ್ ಇನ್ ಮಾಡಿ...',
            thinking: 'ಯೋಚಿಸುತ್ತಿದ್ದೇನೆ...',
            welcomeMessage: 'ನಮಸ್ಕಾರ! 👋 ನಾನು ನಿಮ್ಮ **ಹೋಮ್ಲಿ AI ಸಹಾಯಕ**. ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು, ಗಿಡಮೂಲಿಕೆ ಚಹಾಗಳು, ಅಥವಾ ಆರೋಗ್ಯ ಸಲಹೆಗಳ ಬಗ್ಗೆ ನನ್ನನ್ನು ಕೇಳಿ!',
            suggestedQuestions: 'ಈ ರೀತಿ ಕೇಳಿ ನೋಡಿ:',
            voiceInput: 'ಧ್ವನಿ ಇನ್‌ಪುಟ್',
            send: 'ಕಳುಹಿಸಿ',
            newChat: 'ಹೊಸ ಚಾಟ್',
            fullScreen: 'ಪೂರ್ಣ ಪರದೆ',
            openFullPage: 'ಪೂರ್ಣ ಪುಟ ತೆರೆಯಿರಿ',
            minimize: 'ಚಿಕ್ಕದು ಮಾಡಿ',
            expand: 'ವಿಸ್ತರಿಸಿ',
            close: 'ಮುಚ್ಚಿ',
            clearChat: 'ಚಾಟ್ ತೆರವುಗೊಳಿಸಿ',
            medicalDisclaimer: 'ವೈದ್ಯಕೀಯ ಹಕ್ಕುತ್ಯಾಗ',
            disclaimer: 'ಹಕ್ಕುತ್ಯಾಗ',
            disclaimerText: 'ಈ AI ಸಾಮಾನ್ಯ ಆರೋಗ್ಯ ಮಾಹಿತಿಯನ್ನು ಮಾತ್ರ ಒದಗಿಸುತ್ತದೆ. ಇದು ವೃತ್ತಿಪರ ವೈದ್ಯಕೀಯ ಸಲಹೆ, ರೋಗನಿರ್ಣಯ ಅಥವಾ ಚಿಕಿತ್ಸೆಗೆ ಪರ್ಯಾಯವಲ್ಲ.',
            dismiss: 'ವಜಾಗೊಳಿಸಿ',
            enterToSend: 'ಕಳುಹಿಸಲು Enter ಒತ್ತಿ, ಹೊಸ ಸಾಲಿಗೆ Shift+Enter',
            loginRequired: 'AI ಸಹಾಯಕ ಬಳಸಲು ದಯವಿಟ್ಟು ಲಾಗ್ ಇನ್ ಮಾಡಿ',
            loginToChatWith: 'AI ಸಹಾಯಕದೊಂದಿಗೆ ಚಾಟ್ ಮಾಡಲು.',
            // Suggested questions
            coldFlu: 'ಶೀತ ಮತ್ತು ಜ್ವರಕ್ಕೆ ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳು',
            sleepQuality: 'ನಿದ್ರೆಯ ಗುಣಮಟ್ಟವನ್ನು ಸ್ವಾಭಾವಿಕವಾಗಿ ಹೇಗೆ ಸುಧಾರಿಸುವುದು',
            headache: 'ತಲೆನೋವಿಗೆ ಮನೆಮದ್ದುಗಳು',
            herbalTeas: 'ಜೀರ್ಣಕ್ರಿಯೆಗೆ ಅತ್ಯುತ್ತಮ ಗಿಡಮೂಲಿಕೆ ಚಹಾಗಳು',
            skinCare: 'ನೈಸರ್ಗಿಕ ಚರ್ಮ ಆರೈಕೆ ಸಲಹೆಗಳು',
            immunity: 'ನೈಸರ್ಗಿಕವಾಗಿ ರೋಗನಿರೋಧಕ ಶಕ್ತಿ ಹೆಚ್ಚಿಸಿ',
            chatCleared: 'ಚಾಟ್ ತೆರವುಗೊಳಿಸಲಾಗಿದೆ',
            // Voice input translations
            voiceNotSupported: 'ನಿಮ್ಮ ಬ್ರೌಸರ್‌ನಲ್ಲಿ ಧ್ವನಿ ಇನ್‌ಪುಟ್ ಬೆಂಬಲಿಸುವುದಿಲ್ಲ. Chrome ಅಥವಾ Edge ಪ್ರಯತ್ನಿಸಿ.',
            microphoneDenied: 'ಮೈಕ್ರೊಫೋನ್ ಪ್ರವೇಶ ನಿರಾಕರಿಸಲಾಗಿದೆ. ದಯವಿಟ್ಟು ಮೈಕ್ರೊಫೋನ್ ಪ್ರವೇಶಕ್ಕೆ ಅನುಮತಿಸಿ.',
            voiceError: 'ಧ್ವನಿ ಇನ್‌ಪುಟ್ ದೋಷ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
            voiceRecorded: 'ಧ್ವನಿ ಯಶಸ್ವಿಯಾಗಿ ರೆಕಾರ್ಡ್ ಆಯಿತು!',
            voiceStartFailed: 'ಧ್ವನಿ ಇನ್‌ಪುಟ್ ಪ್ರಾರಂಭಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
            listeningInLanguage: '{lang} ನಲ್ಲಿ ಕೇಳುತ್ತಿದ್ದೇನೆ... ಈಗ ಮಾತನಾಡಿ! ನಿಲ್ಲಿಸಲು ಮೈಕ್ ಕ್ಲಿಕ್ ಮಾಡಿ.',
            // Image translations
            analyzeImage: 'ದಯವಿಟ್ಟು ಈ ಚಿತ್ರವನ್ನು ವಿಶ್ಲೇಷಿಸಿ ಮತ್ತು ನೀವು ಏನು ನೋಡುತ್ತೀರಿ ಎಂದು ಹೇಳಿ.',
            imageAttached: 'ಚಿತ್ರ ಲಗತ್ತಿಸಲಾಗಿದೆ! ನಾನು ಈ ಚಿತ್ರವನ್ನು ವಿಶ್ಲೇಷಿಸಬಲ್ಲೆ.',
            fileTooLarge: 'ಫೈಲ್ ತುಂಬಾ ದೊಡ್ಡದಾಗಿದೆ. ಗರಿಷ್ಠ ಗಾತ್ರ 5MB.',
            unsupportedFileType: 'ಬೆಂಬಲಿಸದ ಫೈಲ್ ಪ್ರಕಾರ. ದಯವಿಟ್ಟು ಚಿತ್ರಗಳು ಅಥವಾ PDF ಫೈಲ್‌ಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.',
            // Rate limiting translations
            requestsRemaining: 'ಇಂದು ಉಳಿದಿರುವ ವಿನಂತಿಗಳು',
            unlimitedRequests: 'ಅನಿಯಮಿತ ವಿನಂತಿಗಳು',
            limitReached: 'ದೈನಿಕ ಮಿತಿ ತಲುಪಿದೆ',
            limitReachedDesc: 'ನೀವು ಇಂದಿಗೆ ಎಲ್ಲಾ 5 AI ವಿನಂತಿಗಳನ್ನು ಬಳಸಿದ್ದೀರಿ. ಮಿತಿ ಮಧ್ಯರಾತ್ರಿ ಮರುಹೊಂದಿಸಲಾಗುತ್ತದೆ.',
        },
        
        // Profile
        profile: {
            title: 'ನನ್ನ ಪ್ರೊಫೈಲ್',
            editProfile: 'ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ',
            myRemedies: 'ನನ್ನ ಪರಿಹಾರಗಳು',
            savedRemedies: 'ಉಳಿಸಿದ ಪರಿಹಾರಗಳು',
            followers: 'ಅನುಯಾಯಿಗಳು',
            following: 'ಅನುಸರಿಸುತ್ತಿರುವವರು',
            memberSince: 'ಸದಸ್ಯತ್ವ ದಿನಾಂಕ',
            bio: 'ಬಯೋ',
            location: 'ಸ್ಥಳ',
            website: 'ವೆಬ್‌ಸೈಟ್',
            noRemedies: 'ಇನ್ನೂ ಯಾವುದೇ ಪರಿಹಾರಗಳಿಲ್ಲ',
            noSaved: 'ಉಳಿಸಿದ ಪರಿಹಾರಗಳಿಲ್ಲ',
            changePicture: 'ಚಿತ್ರ ಬದಲಾಯಿಸಿ',
            changeBanner: 'ಬ್ಯಾನರ್ ಬದಲಾಯಿಸಿ',
            deleteAccount: 'ಖಾತೆ ಅಳಿಸಿ',
            overview: 'ಅವಲೋಕನ',
            favorites: 'ಮೆಚ್ಚಿನವುಗಳು',
            noFavorites: 'ಇನ್ನೂ ಯಾವುದೇ ಮೆಚ್ಚಿನವುಗಳಿಲ್ಲ',
            reviewsGiven: 'ನೀಡಿದ ರಿವ್ಯೂಗಳು',
            remediesCreated: 'ರಚಿಸಿದ ಪರಿಹಾರಗಳು',
            profileInformation: 'ಪ್ರೊಫೈಲ್ ಮಾಹಿತಿ',
            edit: 'ಸಂಪಾದಿಸಿ',
            fullName: 'ಪೂರ್ಣ ಹೆಸರು',
            email: 'ಇಮೇಲ್',
            emailAddress: 'ಇಮೇಲ್ ವಿಳಾಸ',
            emailCannotChange: 'ಇಮೇಲ್ ಬದಲಾಯಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ',
            saveChanges: 'ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',
            cancel: 'ರದ್ದುಮಾಡಿ',
            noSavedDescription: 'ನಂತರ ತ್ವರಿತವಾಗಿ ಪ್ರವೇಶಿಸಲು ಪರಿಹಾರಗಳನ್ನು ಉಳಿಸಿ',
            browseRemedies: 'ಪರಿಹಾರಗಳನ್ನು ನೋಡಿ',
            noFavoritesDescription: 'ಪರಿಹಾರಗಳನ್ನು ಮೆಚ್ಚಿನವುಗಳಾಗಿ ಗುರುತಿಸಿ',
            discoverRemedies: 'ಪರಿಹಾರಗಳನ್ನು ಕಂಡುಕೊಳ್ಳಿ',
            loadingProfile: 'ಪ್ರೊಫೈಲ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
            clickToEditBanner: 'ಬ್ಯಾನರ್ ಸಂಪಾದಿಸಲು ಕ್ಲಿಕ್ ಮಾಡಿ',
            delete: 'ಅಳಿಸಿ',
            crop: 'ಕ್ರಾಪ್',
            banner: 'ಬ್ಯಾನರ್',
            image: 'ಚಿತ್ರ',
            zoom: 'ಜೂಮ್',
            save: 'ಉಳಿಸಿ',
            accountInfo: 'ಖಾತೆ ಮಾಹಿತಿ',
            noMyRemedies: 'ಇನ್ನೂ ಯಾವುದೇ ಪರಿಹಾರಗಳನ್ನು ರಚಿಸಿಲ್ಲ',
            noMyRemediesDescription: 'ನಿಮ್ಮ ಜ್ಞಾನವನ್ನು ಇತರರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಲು ಪರಿಹಾರಗಳನ್ನು ರಚಿಸಿ',
            createRemedy: 'ಪರಿಹಾರ ರಚಿಸಿ',
            pending: 'ಬಾಕಿ',
            approved: 'ಅನುಮೋದಿಸಲಾಗಿದೆ',
            rejected: 'ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
        },
        
        // Admin Panel
        admin: {
            title: 'ಅಡ್ಮಿನ್ ಪ್ಯಾನೆಲ್',
            subtitle: 'ಬಾಕಿ ಇರುವ ಪರಿಹಾರ ಸಲ್ಲಿಕೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
            pendingRemedies: 'ಬಾಕಿ ಪರಿಹಾರಗಳು ಪರಿಶೀಲನೆಗಾಗಿ ಕಾಯುತ್ತಿವೆ',
            pendingRemedy: 'ಬಾಕಿ ಪರಿಹಾರ ಪರಿಶೀಲನೆಗಾಗಿ ಕಾಯುತ್ತಿದೆ',
            refresh: 'ರಿಫ್ರೆಶ್',
            allCaughtUp: 'ಎಲ್ಲಾ ಮುಗಿದಿದೆ!',
            noPendingRemedies: 'ಪರಿಶೀಲಿಸಲು ಯಾವುದೇ ಬಾಕಿ ಪರಿಹಾರಗಳಿಲ್ಲ.',
            approve: 'ಅನುಮೋದಿಸಿ',
            reject: 'ತಿರಸ್ಕರಿಸಿ',
            approveSuccess: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ಅನುಮೋದಿಸಲಾಗಿದೆ!',
            rejectSuccess: 'ಪರಿಹಾರ ತಿರಸ್ಕರಿಸಲಾಗಿದೆ',
            rejectRemedy: 'ಪರಿಹಾರ ತಿರಸ್ಕರಿಸಿ',
            rejectionReason: 'ತಿರಸ್ಕಾರಕ್ಕೆ ಕಾರಣ ನೀಡಿ (ಐಚ್ಛಿಕ):',
            rejectionPlaceholder: 'ಉದಾ., ಅಪೂರ್ಣ ಮಾಹಿತಿ, ಸುರಕ್ಷತೆ ಕಾಳಜಿಗಳು...',
            confirmReject: 'ತಿರಸ್ಕಾರ ದೃಢೀಕರಿಸಿ',
            cancel: 'ರದ್ದುಮಾಡಿ',
            accessDenied: 'ಪ್ರವೇಶ ನಿರಾಕರಿಸಲಾಗಿದೆ. ಅಡ್ಮಿನ್ ಮಾತ್ರ.',
            createdBy: 'ರಚಿಸಿದವರು',
            unknownUser: 'ಅಪರಿಚಿತ',
        },
        
        // Settings
        settings: {
            title: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
            subtitle: 'ನಿಮ್ಮ ಆದ್ಯತೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
            language: 'ಭಾಷೆ',
            languageDesc: 'ನಿಮ್ಮ ಆದ್ಯತೆಯ ಭಾಷೆ ಆಯ್ಕೆಮಾಡಿ',
            english: 'English (ಆಂಗ್ಲ)',
            hindi: 'हिंदी (ಹಿಂದಿ)',
            kannada: 'ಕನ್ನಡ',
            theme: 'ಥೀಮ್',
            themeDesc: 'ಲೈಟ್ ಅಥವಾ ಡಾರ್ಕ್ ಮೋಡ್ ಆಯ್ಕೆಮಾಡಿ',
            lightMode: 'ಲೈಟ್ ಮೋಡ್',
            darkMode: 'ಡಾರ್ಕ್ ಮೋಡ್',
            switchToLight: 'ಲೈಟ್ ಮೋಡ್‌ಗೆ ಬದಲಿಸಿ',
            switchToDark: 'ಡಾರ್ಕ್ ಮೋಡ್‌ಗೆ ಬದಲಿಸಿ',
            systemDefault: 'ಸಿಸ್ಟಮ್ ಡೀಫಾಲ್ಟ್',
            notifications: 'ಅಧಿಸೂಚನೆಗಳು',
            notificationsDesc: 'ನಿಮ್ಮ ಅಧಿಸೂಚನೆ ಆದ್ಯತೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
            emailNotifications: 'ಇಮೇಲ್ ಅಧಿಸೂಚನೆಗಳು',
            pushNotifications: 'ಪುಶ್ ಅಧಿಸೂಚನೆಗಳು',
            privacy: 'ಗೌಪ್ಯತೆ',
            privacyDesc: 'ನಿಮ್ಮ ಗೌಪ್ಯತೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ನಿಯಂತ್ರಿಸಿ',
            profileVisibility: 'ಪ್ರೊಫೈಲ್ ಗೋಚರತೆ',
            showEmail: 'ಇಮೇಲ್ ತೋರಿಸಿ',
            account: 'ಖಾತೆ',
            accountDesc: 'ನಿಮ್ಮ ಖಾತೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
            changePassword: 'ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಿ',
            exportData: 'ನನ್ನ ಡೇಟಾ ರಫ್ತು ಮಾಡಿ',
            deleteAccount: 'ಖಾತೆ ಅಳಿಸಿ',
            saveChanges: 'ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',
            saved: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು ಉಳಿಸಲಾಗಿದೆ!',
        },
        
        // Auth
        auth: {
            welcomeBack: 'ಮರಳಿ ಸ್ವಾಗತ! 👋',
            signInToContinue: 'ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಯಾಣ ಮುಂದುವರಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ',
            loginTitle: 'ಮರಳಿ ಸ್ವಾಗತ',
            loginSubtitle: 'ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಯಾಣ ಮುಂದುವರಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ',
            signupTitle: 'ಹೋಮ್ಲಿಗೆ ಸೇರಿ',
            signupTitle: 'ಖಾತೆ ರಚಿಸಿ 🌿',
            signupSubtitle: 'ಇಂದು ನಿಮ್ಮ ನೈಸರ್ಗಿಕ ಆರೋಗ್ಯ ಪ್ರಯಾಣ ಪ್ರಾರಂಭಿಸಿ',
            email: 'ಇಮೇಲ್',
            emailAddress: 'ಇಮೇಲ್ ವಿಳಾಸ',
            emailPlaceholder: 'ನೀವು@ಉದಾಹರಣೆ.com',
            password: 'ಪಾಸ್‌ವರ್ಡ್',
            passwordPlaceholder: 'ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ನಮೂದಿಸಿ',
            createPassword: 'ಬಲವಾದ ಪಾಸ್‌ವರ್ಡ್ ರಚಿಸಿ',
            confirmPassword: 'ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ',
            confirmPasswordPlaceholder: 'ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ',
            fullName: 'ಪೂರ್ಣ ಹೆಸರು',
            fullNamePlaceholder: 'ಜಾನ್ ಡೋ',
            rememberMe: 'ನನ್ನನ್ನು ನೆನಪಿಟ್ಟುಕೊಳ್ಳಿ',
            forgotPassword: 'ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿದ್ದೀರಾ?',
            noAccount: 'ಖಾತೆ ಇಲ್ಲವೇ?',
            hasAccount: 'ಈಗಾಗಲೇ ಖಾತೆ ಇದೆಯೇ?',
            signIn: 'ಸೈನ್ ಇನ್ ಮಾಡಿ',
            signUp: 'ಖಾತೆ ರಚಿಸಿ',
            signInWith: 'ಮೂಲಕ ಸೈನ್ ಇನ್ ಮಾಡಿ',
            signUpWith: 'ಮೂಲಕ ಸೈನ್ ಅಪ್ ಮಾಡಿ',
            orContinueWith: 'ಅಥವಾ ಮುಂದುವರಿಸಿ',
            signUpFree: 'ಉಚಿತ ಸೈನ್ ಅಪ್ ಮಾಡಿ',
            createAccount: 'ನಿಮ್ಮ ಉಚಿತ ಖಾತೆ ರಚಿಸಿ',
            verifyEmail: 'ನಿಮ್ಮ ಇಮೇಲ್ ಪರಿಶೀಲಿಸಿ',
            otpSent: 'ನಾವು ನಿಮ್ಮ ಇಮೇಲ್‌ಗೆ ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ',
            enterOtp: 'OTP ನಮೂದಿಸಿ',
            resendOtp: 'OTP ಮರುಕಳುಹಿಸಿ',
            resetPassword: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
            sendResetLink: 'ಮರುಹೊಂದಿಸುವ ಲಿಂಕ್ ಕಳುಹಿಸಿ',
            newPassword: 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್',
            newPasswordPlaceholder: 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ನಮೂದಿಸಿ',
            resetSuccess: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವಿಕೆ ಯಶಸ್ವಿಯಾಗಿದೆ!',
            backToLogin: 'ಲಾಗಿನ್‌ಗೆ ಹಿಂತಿರುಗಿ',
            emailRequired: 'ಇಮೇಲ್ ಅಗತ್ಯವಿದೆ',
            validEmail: 'ದಯವಿಟ್ಟು ಮಾನ್ಯ ಇಮೇಲ್ ನಮೂದಿಸಿ',
            passwordRequired: 'ಪಾಸ್‌ವರ್ಡ್ ಅಗತ್ಯವಿದೆ',
            passwordMin: 'ಪಾಸ್‌ವರ್ಡ್ ಕನಿಷ್ಠ 6 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
            nameRequired: 'ಹೆಸರು ಅಗತ್ಯವಿದೆ',
            nameMin: 'ಹೆಸರು ಕನಿಷ್ಠ 2 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
            confirmPasswordRequired: 'ದಯವಿಟ್ಟು ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ',
            passwordsMatch: 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
            enterEmailToReset: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಲು ನಿಮ್ಮ ಇಮೇಲ್ ನಮೂದಿಸಿ',
            checkEmailForReset: 'ಮರುಹೊಂದಿಸುವ ಸೂಚನೆಗಳಿಗಾಗಿ ನಿಮ್ಮ ಇಮೇಲ್ ಪರಿಶೀಲಿಸಿ',
            otpTitle: 'ನಿಮ್ಮ ಖಾತೆ ಪರಿಶೀಲಿಸಿ',
            otpSubtitle: 'ಕಳುಹಿಸಿದ 6-ಅಂಕಿಯ ಕೋಡ್ ನಮೂದಿಸಿ',
            verifyOtp: 'OTP ಪರಿಶೀಲಿಸಿ',
            resendIn: 'ಮರುಕಳುಹಿಸಲು',
            seconds: 'ಸೆಕೆಂಡುಗಳು',
            startJourney: 'ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಯಾಣ ಪ್ರಾರಂಭಿಸಿ ✨',
            joinCommunity: 'ನೈಸರ್ಗಿಕ ಆರೋಗ್ಯ ಉತ್ಸಾಹಿಗಳ ಸಮುದಾಯಕ್ಕೆ ಸೇರಿ',
            atLeast6Chars: 'ಕನಿಷ್ಠ 6 ಅಕ್ಷರಗಳು',
            oneUppercase: 'ಒಂದು ದೊಡ್ಡ ಅಕ್ಷರ',
            oneLowercase: 'ಒಂದು ಸಣ್ಣ ಅಕ್ಷರ',
            oneNumber: 'ಒಂದು ಸಂಖ್ಯೆ',
            termsAgree: 'ಖಾತೆ ರಚಿಸುವ ಮೂಲಕ, ನೀವು ಸಮ್ಮತಿಸುತ್ತೀರಿ',
            termsOfService: 'ಸೇವಾ ನಿಯಮಗಳು',
            and: 'ಮತ್ತು',
            privacyPolicy: 'ಗೌಪ್ಯತೆ ನೀತಿ',
            loginRequired: 'ಪರಿಹಾರ ರಚಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
        },
        
        // Errors
        errors: {
            required: 'ಈ ಕ್ಷೇತ್ರ ಅಗತ್ಯವಿದೆ',
            invalidEmail: 'ದಯವಿಟ್ಟು ಮಾನ್ಯ ಇಮೇಲ್ ನಮೂದಿಸಿ',
            passwordMin: 'ಪಾಸ್‌ವರ್ಡ್ ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
            passwordMatch: 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
            somethingWrong: 'ಏನೋ ತಪ್ಪಾಗಿದೆ',
            tryAgain: 'ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
            notFound: 'ಪುಟ ಕಂಡುಬಂದಿಲ್ಲ',
            unauthorized: 'ಮುಂದುವರಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
            forbidden: 'ನಿಮಗೆ ಅನುಮತಿ ಇಲ್ಲ',
        },
        
        // Success messages
        success: {
            remedyCreated: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ರಚಿಸಲಾಗಿದೆ!',
            remedyUpdated: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ!',
            remedyDeleted: 'ಪರಿಹಾರ ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ!',
            profileUpdated: 'ಪ್ರೊಫೈಲ್ ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ!',
            passwordChanged: 'ಪಾಸ್‌ವರ್ಡ್ ಯಶಸ್ವಿಯಾಗಿ ಬದಲಾಯಿಸಲಾಗಿದೆ!',
            loggedOut: 'ಯಶಸ್ವಿಯಾಗಿ ಲಾಗ್ ಔಟ್ ಆಗಿದೆ!',
            welcomeBackUser: 'ಮರಳಿ ಸ್ವಾಗತ, {name}!',
            accountCreated: 'ಖಾತೆ ಯಶಸ್ವಿಯಾಗಿ ರಚಿಸಲಾಗಿದೆ!',
            registrationSuccessful: 'ನೋಂದಣಿ ಯಶಸ್ವಿ! ದಯವಿಟ್ಟು ನಿಮ್ಮ ಇಮೇಲ್ ಪರಿಶೀಲಿಸಿ.',
            pleaseVerifyEmail: 'ಮುಂದುವರಿಸಲು ದಯವಿಟ್ಟು ನಿಮ್ಮ ಇಮೇಲ್ ಪರಿಶೀಲಿಸಿ.',
            emailVerified: 'ಇಮೇಲ್ ಯಶಸ್ವಿಯಾಗಿ ಪರಿಶೀಲಿಸಲಾಗಿದೆ!',
            otpSentSuccess: 'OTP ಯಶಸ್ವಿಯಾಗಿ ಕಳುಹಿಸಲಾಗಿದೆ!',
            passwordResetOtpSent: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವ OTP ನಿಮ್ಮ ಇಮೇಲ್‌ಗೆ ಕಳುಹಿಸಲಾಗಿದೆ.',
            passwordResetSuccess: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸುವಿಕೆ ಯಶಸ್ವಿ! ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ.',
        },
        
        // Footer
        footer: {
            description: 'ಆಧುನಿಕ ಆರೋಗ್ಯಕ್ಕಾಗಿ ಸಾಂಪ್ರದಾಯಿಕ ಜ್ಞಾನವನ್ನು ಸಂರಕ್ಷಿಸುವುದು. ತಲೆಮಾರುಗಳಿಂದ ಬಂದ ನೈಸರ್ಗಿಕ ಮನೆ ಮದ್ದುಗಳನ್ನು ಕಂಡುಕೊಳ್ಳಿ.',
            explore: 'ಅನ್ವೇಷಿಸಿ',
            allRemedies: 'ಎಲ್ಲಾ ಪರಿಹಾರಗಳು',
            categories: 'ವರ್ಗಗಳು',
            popular: 'ಜನಪ್ರಿಯ',
            latest: 'ಇತ್ತೀಚಿನ',
            categoriesTitle: 'ವರ್ಗಗಳು',
            immunityBoosters: 'ರೋಗ ನಿರೋಧಕ ಶಕ್ತಿ ಹೆಚ್ಚಿಸುವವು',
            digestiveHealth: 'ಜೀರ್ಣ ಆರೋಗ್ಯ',
            coldAndCough: 'ಶೀತ ಮತ್ತು ಕೆಮ್ಮು',
            skinCare: 'ಚರ್ಮ ಆರೈಕೆ',
            support: 'ಬೆಂಬಲ',
            aboutUs: 'ನಮ್ಮ ಬಗ್ಗೆ',
            contact: 'ಸಂಪರ್ಕಿಸಿ',
            faq: 'FAQ',
            privacyPolicy: 'ಗೌಪ್ಯತಾ ನೀತಿ',
            copyright: 'ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.',
            madeWith: 'ಮಾಡಲಾಗಿದೆ',
            forWellness: 'ಆರೋಗ್ಯಕ್ಕಾಗಿ',
            terms: 'ನಿಯಮಗಳು',
            privacy: 'ಗೌಪ್ಯತೆ',
            cookies: 'ಕುಕೀಗಳು',
        },
        
        // About Page
        about: {
            title: 'ನಮ್ಮ ಬಗ್ಗೆ',
            subtitle: 'ಹೋಮ್ಲಿಯ ಕಥೆ ಮತ್ತು ಸಾಂಪ್ರದಾಯಿಕ ಆರೋಗ್ಯ ಜ್ಞಾನವನ್ನು ಸಂರಕ್ಷಿಸುವ ನಮ್ಮ ಧ್ಯೇಯದ ಬಗ್ಗೆ ತಿಳಿಯಿರಿ.',
            storyTitle: 'ನಮ್ಮ ಕಥೆ',
            storyP1: 'ಹೋಮ್ಲಿ ಒಂದು ಸರಳ ನಂಬಿಕೆಯಿಂದ ಹುಟ್ಟಿಕೊಂಡಿತು: ನಮ್ಮ ಅಜ್ಜ-ಅಜ್ಜಿಯರ ಜ್ಞಾನವನ್ನು ಸಂರಕ್ಷಿಸಿ ಹಂಚಿಕೊಳ್ಳಬೇಕು. ಸಿಂಥೆಟಿಕ್ ಪರಿಹಾರಗಳ ಮೇಲೆ ಅವಲಂಬಿತ ಜಗತ್ತಿನಲ್ಲಿ, ಸಾಂಪ್ರದಾಯಿಕ ಮನೆ ಮದ್ದುಗಳು ಬೆಳೆಯಬಹುದಾದ ಸ್ಥಳವನ್ನು ನಾವು ರಚಿಸಲು ಬಯಸಿದ್ದೆವು.',
            storyP2: 'ನಮ್ಮ ವೇದಿಕೆಯು ಶತಮಾನಗಳ ನೈಸರ್ಗಿಕ ಗುಣಪಡಿಸುವ ಜ್ಞಾನವನ್ನು ಒಟ್ಟುಗೂಡಿಸುತ್ತದೆ, ಅದನ್ನು ಎಲ್ಲರಿಗೂ ಪ್ರವೇಶಿಸುವಂತೆ ಮಾಡುತ್ತದೆ.',
            naturalTitle: '100% ನೈಸರ್ಗಿಕ',
            naturalDesc: 'ಎಲ್ಲಾ ಪರಿಹಾರಗಳು ನಿಮ್ಮ ಅಡುಗೆಮನೆಯಲ್ಲಿ ಸಿಗಬಹುದಾದ ನೈಸರ್ಗಿಕ ಪದಾರ್ಥಗಳನ್ನು ಬಳಸುತ್ತವೆ.',
            communityTitle: 'ಸಮುದಾಯ ನಿರ್ವಹಣೆ',
            communityDesc: 'ನಮ್ಮ ಬಳಕೆದಾರರು ಪರಿಹಾರಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳುತ್ತಾರೆ ಮತ್ತು ರೇಟ್ ಮಾಡುತ್ತಾರೆ, ವಿಶ್ವಾಸಾರ್ಹ ಜ್ಞಾನ ಆಧಾರವನ್ನು ನಿರ್ಮಿಸುತ್ತಾರೆ.',
            missionTitle: 'ನಮ್ಮ ಧ್ಯೇಯ',
            missionDesc: 'ಸಾಂಪ್ರದಾಯಿಕ ಆರೋಗ್ಯ ಜ್ಞಾನವನ್ನು ಎಲ್ಲೆಡೆ ಎಲ್ಲರಿಗೂ ಪ್ರವೇಶಿಸುವಂತೆ ಮಾಡುವುದು.',
            careTitle: 'ಕಾಳಜಿಯಿಂದ ನಿರ್ಮಿಸಲಾಗಿದೆ',
            careDesc: 'ಪ್ರಕಟಣೆಗೆ ಮೊದಲು ಪ್ರತಿ ಪರಿಹಾರವನ್ನು ಸುರಕ್ಷತೆಗಾಗಿ ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ.',
            teamTitle: 'ಉತ್ಸಾಹದಿಂದ ನಿರ್ಮಿಸಲಾಗಿದೆ',
            teamDesc: 'ಹೋಮ್ಲಿಯು ಭವಿಷ್ಯದ ಪೀಳಿಗೆಗಳಿಗಾಗಿ ನಮ್ಮ ಪೂರ್ವಜರ ಗುಣಪಡಿಸುವ ಸಂಪ್ರದಾಯಗಳನ್ನು ಸಂರಕ್ಷಿಸಲು ಸಮರ್ಪಿತ ಉತ್ಸಾಹ ಯೋಜನೆಯಾಗಿದೆ.',
        },
        
        // Contact Page
        contact: {
            title: 'ಸಂಪರ್ಕಿಸಿ',
            subtitle: 'ಪ್ರಶ್ನೆಗಳು ಅಥವಾ ಸಲಹೆಗಳಿವೆಯೇ? ನಿಮ್ಮಿಂದ ಕೇಳಲು ನಾವು ಇಷ್ಟಪಡುತ್ತೇವೆ!',
            formTitle: 'ನಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಿ',
            nameLabel: 'ನಿಮ್ಮ ಹೆಸರು',
            namePlaceholder: 'ಜಾನ್ ಡೋ',
            emailLabel: 'ಇಮೇಲ್ ವಿಳಾಸ',
            emailPlaceholder: 'you@example.com',
            subjectLabel: 'ವಿಷಯ',
            subjectPlaceholder: 'ನಾವು ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?',
            messageLabel: 'ಸಂದೇಶ',
            messagePlaceholder: 'ನಿಮ್ಮ ಮನಸ್ಸಿನಲ್ಲಿ ಏನಿದೆ ಎಂದು ನಮಗೆ ತಿಳಿಸಿ...',
            sendButton: 'ಸಂದೇಶ ಕಳುಹಿಸಿ',
            successMessage: 'ಸಂದೇಶ ಯಶಸ್ವಿಯಾಗಿ ಕಳುಹಿಸಲಾಗಿದೆ! ನಾವು ಶೀಘ್ರದಲ್ಲೇ ಸಂಪರ್ಕಿಸುತ್ತೇವೆ.',
            infoTitle: 'ಸಂಪರ್ಕದಲ್ಲಿರಿ',
            email: 'ಇಮೇಲ್',
            location: 'ಸ್ಥಳ',
            followUs: 'ನಮ್ಮನ್ನು ಅನುಸರಿಸಿ',
        },
        
        // FAQ Page
        faq: {
            title: 'ಪದೇ ಪದೇ ಕೇಳುವ ಪ್ರಶ್ನೆಗಳು',
            subtitle: 'ಹೋಮ್ಲಿ ಮತ್ತು ನೈಸರ್ಗಿಕ ಪರಿಹಾರಗಳ ಬಗ್ಗೆ ಸಾಮಾನ್ಯ ಪ್ರಶ್ನೆಗಳಿಗೆ ಉತ್ತರಗಳನ್ನು ಹುಡುಕಿ.',
            searchPlaceholder: 'ಪ್ರಶ್ನೆಗಳನ್ನು ಹುಡುಕಿ...',
            q1: 'ಹೋಮ್ಲಿ ಎಂದರೇನು?',
            a1: 'ಹೋಮ್ಲಿಯು ಸಾಂಪ್ರದಾಯಿಕ ಮನೆ ಮದ್ದುಗಳನ್ನು ಸಂರಕ್ಷಿಸಲು ಮತ್ತು ಹಂಚಿಕೊಳ್ಳಲು ಸಮರ್ಪಿತ ವೇದಿಕೆಯಾಗಿದೆ.',
            q2: 'ಈ ಪರಿಹಾರಗಳನ್ನು ಬಳಸಲು ಸುರಕ್ಷಿತವೇ?',
            a2: 'ಸಾಂಪ್ರದಾಯಿಕ ಪರಿಹಾರಗಳು ಸಾಮಾನ್ಯವಾಗಿ ಸುರಕ್ಷಿತವಾಗಿವೆ, ಆದರೆ ಯಾವುದೇ ಹೊಸ ಚಿಕಿತ್ಸೆಯನ್ನು ಪ್ರಯತ್ನಿಸುವ ಮೊದಲು ಆರೋಗ್ಯ ವೃತ್ತಿಪರರನ್ನು ಸಂಪರ್ಕಿಸಲು ನಾವು ಶಿಫಾರಸು ಮಾಡುತ್ತೇವೆ.',
            q3: 'ನನ್ನ ಸ್ವಂತ ಪರಿಹಾರಗಳನ್ನು ಕೊಡುಗೆ ನೀಡಬಹುದೇ?',
            a3: 'ಹೌದು! ಖಾತೆ ರಚಿಸಿದ ನಂತರ, ನಿಮ್ಮ ಕುಟುಂಬದ ಸಾಂಪ್ರದಾಯಿಕ ಪರಿಹಾರಗಳನ್ನು ನಮ್ಮ ಸಮುದಾಯದೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಬಹುದು.',
            q4: 'ಹೋಮ್ಲಿ ಉಚಿತವೇ?',
            a4: 'ಹೌದು, ಹೋಮ್ಲಿ ಸಂಪೂರ್ಣವಾಗಿ ಉಚಿತ. ನೀವು ಯಾವುದೇ ವೆಚ್ಚವಿಲ್ಲದೆ ಪರಿಹಾರಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಬಹುದು, ಹುಡುಕಬಹುದು ಮತ್ತು ಉಳಿಸಬಹುದು.',
            q5: 'ನನ್ನ ನೆಚ್ಚಿನ ಪರಿಹಾರಗಳನ್ನು ಹೇಗೆ ಉಳಿಸುವುದು?',
            a5: 'ಯಾವುದೇ ಪರಿಹಾರ ಕಾರ್ಡ್‌ನಲ್ಲಿ ಬುಕ್‌ಮಾರ್ಕ್ ಐಕಾನ್ ಕ್ಲಿಕ್ ಮಾಡಿ ನಿಮ್ಮ ಮೆಚ್ಚಿನವುಗಳಿಗೆ ಉಳಿಸಿ.',
            q6: 'AI ಸಹಾಯಕ ಎಂದರೇನು?',
            a6: 'ನಮ್ಮ AI ಸಹಾಯಕ ನಿಮ್ಮ ರೋಗಲಕ್ಷಣಗಳ ಆಧಾರದ ಮೇಲೆ ಪರಿಹಾರಗಳನ್ನು ಹುಡುಕಲು ಸಹಾಯ ಮಾಡಬಹುದು.',
            q7: 'ಪರಿಹಾರದೊಂದಿಗೆ ಸಮಸ್ಯೆಯನ್ನು ಹೇಗೆ ವರದಿ ಮಾಡುವುದು?',
            a7: 'ತಪ್ಪಾದ ಅಥವಾ ಹಾನಿಕಾರಕ ಮಾಹಿತಿ ಕಂಡರೆ, ದಯವಿಟ್ಟು ಸಂಪರ್ಕ ಫಾರ್ಮ್ ಬಳಸಿ ವರದಿ ಮಾಡಿ.',
            q8: 'ನನ್ನ ವೈಯಕ್ತಿಕ ಡೇಟಾ ಸುರಕ್ಷಿತವೇ?',
            a8: 'ಹೌದು, ನಾವು ಗೌಪ್ಯತೆಯನ್ನು ಗಂಭೀರವಾಗಿ ಪರಿಗಣಿಸುತ್ತೇವೆ. ನಾವು ಅಗತ್ಯ ಮಾಹಿತಿಯನ್ನು ಮಾತ್ರ ಸಂಗ್ರಹಿಸುತ್ತೇವೆ.',
            noResults: 'ನಿಮ್ಮ ಹುಡುಕಾಟಕ್ಕೆ ಹೊಂದಿಕೆಯಾಗುವ ಪ್ರಶ್ನೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ.',
            stillHaveQuestions: 'ಇನ್ನೂ ಪ್ರಶ್ನೆಗಳಿವೆಯೇ?',
            contactUs: 'ನೀವು ಹುಡುಕುತ್ತಿರುವುದನ್ನು ಕಂಡುಹಿಡಿಯಲಾಗಲಿಲ್ಲವೇ? ನಮ್ಮನ್ನು ನೇರವಾಗಿ ಸಂಪರ್ಕಿಸಿ.',
            getInTouch: 'ಸಂಪರ್ಕಿಸಿ',
        },
        
        // Privacy Page
        privacy: {
            title: 'ಗೌಪ್ಯತಾ ನೀತಿ',
            subtitle: 'ನಿಮ್ಮ ಗೌಪ್ಯತೆ ನಮಗೆ ಮುಖ್ಯ. ನಿಮ್ಮ ಮಾಹಿತಿಯನ್ನು ನಾವು ಹೇಗೆ ಸಂಗ್ರಹಿಸುತ್ತೇವೆ ಮತ್ತು ಬಳಸುತ್ತೇವೆ ಎಂಬುದನ್ನು ತಿಳಿಯಿರಿ.',
            lastUpdated: 'ಕೊನೆಯ ನವೀಕರಣ',
            intro: 'ಹೋಮ್ಲಿಯಲ್ಲಿ, ನಿಮ್ಮ ಗೌಪ್ಯತೆಯನ್ನು ರಕ್ಷಿಸಲು ನಾವು ಬದ್ಧರಾಗಿದ್ದೇವೆ.',
            dataCollectionTitle: 'ನಾವು ಯಾವ ಮಾಹಿತಿ ಸಂಗ್ರಹಿಸುತ್ತೇವೆ',
            dataCollectionContent: 'ನಿಮ್ಮ ಹೆಸರು ಮತ್ತು ಇಮೇಲ್ ವಿಳಾಸದಂತಹ ನೀವು ನೇರವಾಗಿ ಒದಗಿಸುವ ಮಾಹಿತಿಯನ್ನು ನಾವು ಸಂಗ್ರಹಿಸುತ್ತೇವೆ.',
            dataUsageTitle: 'ನಿಮ್ಮ ಮಾಹಿತಿಯನ್ನು ನಾವು ಹೇಗೆ ಬಳಸುತ್ತೇವೆ',
            dataUsageContent: 'ನಮ್ಮ ಸೇವೆಗಳನ್ನು ಒದಗಿಸಲು ಮತ್ತು ಸುಧಾರಿಸಲು, ನಿಮ್ಮ ಅನುಭವವನ್ನು ವೈಯಕ್ತೀಕರಿಸಲು ನಿಮ್ಮ ಮಾಹಿತಿಯನ್ನು ನಾವು ಬಳಸುತ್ತೇವೆ.',
            dataProtectionTitle: 'ನಿಮ್ಮ ಡೇಟಾವನ್ನು ನಾವು ಹೇಗೆ ರಕ್ಷಿಸುತ್ತೇವೆ',
            dataProtectionContent: 'ನಿಮ್ಮ ಡೇಟಾವನ್ನು ರಕ್ಷಿಸಲು ನಾವು ಉದ್ಯಮ-ಮಾನದಂಡ ಭದ್ರತಾ ಕ್ರಮಗಳನ್ನು ಅಳವಡಿಸುತ್ತೇವೆ.',
            yourRightsTitle: 'ನಿಮ್ಮ ಹಕ್ಕುಗಳು',
            yourRightsContent: 'ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಡೇಟಾವನ್ನು ಪ್ರವೇಶಿಸಲು, ಸರಿಪಡಿಸಲು ಅಥವಾ ಅಳಿಸಲು ನಿಮಗೆ ಹಕ್ಕಿದೆ.',
            questionsTitle: 'ಪ್ರಶ್ನೆಗಳಿವೆಯೇ?',
            questionsContent: 'ನಮ್ಮ ಗೌಪ್ಯತಾ ಅಭ್ಯಾಸಗಳ ಬಗ್ಗೆ ಯಾವುದೇ ಪ್ರಶ್ನೆಗಳಿದ್ದರೆ, ದಯವಿಟ್ಟು ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ.',
        },
        
        // Terms Page
        terms: {
            title: 'ಸೇವಾ ನಿಯಮಗಳು',
            subtitle: 'ಹೋಮ್ಲಿಯನ್ನು ಬಳಸುವ ಮೊದಲು ದಯವಿಟ್ಟು ಈ ನಿಯಮಗಳನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ ಓದಿ.',
            lastUpdated: 'ಕೊನೆಯ ನವೀಕರಣ',
            intro: 'ಹೋಮ್ಲಿಯನ್ನು ಬಳಸುವ ಮೂಲಕ, ನೀವು ಈ ಸೇವಾ ನಿಯಮಗಳಿಗೆ ಒಪ್ಪುತ್ತೀರಿ.',
            acceptanceTitle: 'ನಿಯಮಗಳ ಸ್ವೀಕಾರ',
            acceptanceContent: 'ಹೋಮ್ಲಿಯನ್ನು ಪ್ರವೇಶಿಸುವ ಅಥವಾ ಬಳಸುವ ಮೂಲಕ, ನೀವು ಈ ನಿಯಮಗಳನ್ನು ಓದಿ ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೀರಿ ಎಂದು ಒಪ್ಪಿಕೊಳ್ಳುತ್ತೀರಿ.',
            userAccountsTitle: 'ಬಳಕೆದಾರ ಖಾತೆಗಳು',
            userAccountsContent: 'ನಿಮ್ಮ ಖಾತೆ ರುಜುವಾತುಗಳ ಗೋಪ್ಯತೆಯನ್ನು ಕಾಪಾಡಲು ನೀವು ಜವಾಬ್ದಾರರು.',
            contentTitle: 'ಬಳಕೆದಾರ ವಿಷಯ',
            contentContent: 'ಹೋಮ್ಲಿಯಲ್ಲಿ ನೀವು ರಚಿಸುವ ವಿಷಯದ ಮಾಲೀಕತ್ವ ನಿಮ್ಮಲ್ಲಿಯೇ ಉಳಿಯುತ್ತದೆ.',
            disclaimerTitle: 'ವೈದ್ಯಕೀಯ ಹಕ್ಕು ನಿರಾಕರಣೆ',
            disclaimerContent: 'ಹೋಮ್ಲಿ ಶೈಕ್ಷಣಿಕ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಮಾತ್ರ ಮಾಹಿತಿಯನ್ನು ಒದಗಿಸುತ್ತದೆ. ಯಾವುದೇ ಹೊಸ ಚಿಕಿತ್ಸೆಯನ್ನು ಪ್ರಯತ್ನಿಸುವ ಮೊದಲು ಆರೋಗ್ಯ ಸೇವಾ ಪೂರೈಕೆದಾರರನ್ನು ಸಂಪರ್ಕಿಸಿ.',
            changesTitle: 'ನಿಯಮಗಳಲ್ಲಿ ಬದಲಾವಣೆಗಳು',
            changesContent: 'ಈ ನಿಯಮಗಳನ್ನು ನಾವು ಕಾಲಕಾಲಕ್ಕೆ ನವೀಕರಿಸಬಹುದು.',
        },
        
        // Cookies Page
        cookies: {
            title: 'ಕುಕೀ ನೀತಿ',
            subtitle: 'ಹೋಮ್ಲಿಯಲ್ಲಿ ನಿಮ್ಮ ಅನುಭವವನ್ನು ಸುಧಾರಿಸಲು ನಾವು ಕುಕೀಗಳನ್ನು ಹೇಗೆ ಬಳಸುತ್ತೇವೆ ಎಂಬುದನ್ನು ತಿಳಿಯಿರಿ.',
            whatAreTitle: 'ಕುಕೀಗಳು ಎಂದರೇನು?',
            whatAreContent: 'ಕುಕೀಗಳು ನೀವು ವೆಬ್‌ಸೈಟ್‌ಗೆ ಭೇಟಿ ನೀಡಿದಾಗ ನಿಮ್ಮ ಸಾಧನದಲ್ಲಿ ಸಂಗ್ರಹಿಸಲಾದ ಸಣ್ಣ ಪಠ್ಯ ಫೈಲ್‌ಗಳಾಗಿವೆ.',
            typesTitle: 'ನಾವು ಬಳಸುವ ಕುಕೀ ಪ್ರಕಾರಗಳು',
            essentialTitle: 'ಅಗತ್ಯ ಕುಕೀಗಳು',
            essentialDesc: 'ವೆಬ್‌ಸೈಟ್ ಸರಿಯಾಗಿ ಕಾರ್ಯನಿರ್ವಹಿಸಲು ಈ ಕುಕೀಗಳು ಅಗತ್ಯ.',
            functionalTitle: 'ಕ್ರಿಯಾತ್ಮಕ ಕುಕೀಗಳು',
            functionalDesc: 'ಈ ಕುಕೀಗಳು ನಿಮ್ಮ ಆದ್ಯತೆಗಳನ್ನು ನೆನಪಿಸಿಕೊಳ್ಳುತ್ತವೆ.',
            analyticsTitle: 'ವಿಶ್ಲೇಷಣಾ ಕುಕೀಗಳು',
            analyticsDesc: 'ಸಂದರ್ಶಕರು ನಮ್ಮ ವೆಬ್‌ಸೈಟ್‌ನೊಂದಿಗೆ ಹೇಗೆ ಸಂವಹನ ನಡೆಸುತ್ತಾರೆ ಎಂಬುದನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ನಾವು ಈ ಕುಕೀಗಳನ್ನು ಬಳಸುತ್ತೇವೆ.',
            required: 'ಅಗತ್ಯ',
            optional: 'ಐಚ್ಛಿಕ',
            manageTitle: 'ಕುಕೀಗಳನ್ನು ನಿರ್ವಹಿಸುವುದು',
            manageContent: 'ನಿಮ್ಮ ಬ್ರೌಸರ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳ ಮೂಲಕ ನೀವು ಕುಕೀಗಳನ್ನು ನಿಯಂತ್ರಿಸಬಹುದು.',
            questionsTitle: 'ಪ್ರಶ್ನೆಗಳಿವೆಯೇ?',
            questionsContent: 'ನಮ್ಮ ಕುಕೀ ಅಭ್ಯಾಸಗಳ ಬಗ್ಗೆ ಪ್ರಶ್ನೆಗಳಿದ್ದರೆ, ದಯವಿಟ್ಟು ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ.',
        },
        
        // Popular Page
        popular: {
            title: 'ಜನಪ್ರಿಯ ಪರಿಹಾರಗಳು',
            subtitle: 'ನಮ್ಮ ಸಮುದಾಯದಲ್ಲಿ ಅತ್ಯಂತ ಪ್ರೀತಿಯ ಪರಿಹಾರಗಳನ್ನು ಕಂಡುಕೊಳ್ಳಿ.',
            remediesFound: 'ಜನಪ್ರಿಯ ಪರಿಹಾರಗಳು',
            loginRequired: 'ಲಾಗಿನ್ ಅಗತ್ಯವಿದೆ',
            loginToView: 'ಜನಪ್ರಿಯ ಪರಿಹಾರಗಳನ್ನು ನೋಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ.',
            noRemedies: 'ಯಾವುದೇ ಜನಪ್ರಿಯ ಪರಿಹಾರಗಳಿಲ್ಲ',
            noRemediesDesc: 'ಟ್ರೆಂಡಿಂಗ್ ಪರಿಹಾರಗಳಿಗಾಗಿ ನಂತರ ಪರಿಶೀಲಿಸಿ.',
        },
        
        // Latest Page
        latest: {
            title: 'ಇತ್ತೀಚಿನ ಪರಿಹಾರಗಳು',
            subtitle: 'ನಮ್ಮ ಪರಿಹಾರ ಸಂಗ್ರಹಕ್ಕೆ ಹೊಸ ಸೇರ್ಪಡೆಗಳನ್ನು ಅನ್ವೇಷಿಸಿ.',
            remediesFound: 'ಹೊಸ ಪರಿಹಾರಗಳು',
            loginRequired: 'ಲಾಗಿನ್ ಅಗತ್ಯವಿದೆ',
            loginToView: 'ಇತ್ತೀಚಿನ ಪರಿಹಾರಗಳನ್ನು ನೋಡಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ.',
            noRemedies: 'ಯಾವುದೇ ಹೊಸ ಪರಿಹಾರಗಳಿಲ್ಲ',
            noRemediesDesc: 'ಪರಿಹಾರವನ್ನು ಹಂಚಿಕೊಳ್ಳುವ ಮೊದಲಿಗರಾಗಿ!',
        },
        
        // 404 Page
        notFound: {
            title: '404',
            subtitle: 'ಪುಟ ಕಂಡುಬಂದಿಲ್ಲ',
            description: 'ಅಯ್ಯೋ! ಈ ಪರಿಹಾರ ತೋಟದಲ್ಲಿ ಕಳೆದುಹೋದಂತಿದೆ. ನಿಮ್ಮನ್ನು ಪರಿಚಿತ ಸ್ಥಳಕ್ಕೆ ಮರಳಿ ಕರೆದೊಯ್ಯೋಣ.',
            backHome: 'ಮನೆಗೆ ಹಿಂತಿರುಗಿ',
            goBack: 'ಹಿಂದೆ ಹೋಗಿ',
        },
    },
};

// --------------------------------------------
// LANGUAGE CONTEXT
// --------------------------------------------

const LanguageContext = createContext();

// Language names for display
const languageNames = {
    en: 'English',
    hi: 'हिंदी',
    kn: 'ಕನ್ನಡ',
};

// Language codes to full names (for AI)
const languageFullNames = {
    en: 'English',
    hi: 'Hindi',
    kn: 'Kannada',
};

/**
 * LanguageProvider - Provides language context to the app
 */
export const LanguageProvider = ({ children }) => {
    // Get initial language from localStorage or default to 'en'
    const [language, setLanguage] = useState(() => {
        const savedLanguage = localStorage.getItem('homely-language');
        return savedLanguage && translations[savedLanguage] ? savedLanguage : 'en';
    });

    // Save language to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('homely-language', language);
        // Update document lang attribute
        document.documentElement.lang = language;
    }, [language]);

    /**
     * Get translation by key path (e.g., 'nav.home')
     * Supports interpolation with params object (e.g., t('welcome', { name: 'John' }))
     */
    const t = useCallback((keyPath, paramsOrFallback = '', fallback = '') => {
        // Determine if second argument is params object or fallback string
        const params = typeof paramsOrFallback === 'object' ? paramsOrFallback : null;
        const fallbackStr = typeof paramsOrFallback === 'string' ? paramsOrFallback : fallback;
        
        const keys = keyPath.split('.');
        let value = translations[language];
        
        for (const key of keys) {
            if (value && typeof value === 'object' && key in value) {
                value = value[key];
            } else {
                // Try fallback to English
                let fallbackValue = translations.en;
                for (const k of keys) {
                    if (fallbackValue && typeof fallbackValue === 'object' && k in fallbackValue) {
                        fallbackValue = fallbackValue[k];
                    } else {
                        return fallbackStr || keyPath;
                    }
                }
                value = fallbackValue;
                break;
            }
        }
        
        let result = value || fallbackStr || keyPath;
        
        // Handle interpolation: replace {key} with params[key]
        if (params && typeof result === 'string') {
            Object.keys(params).forEach(key => {
                result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]);
            });
        }
        
        return result;
    }, [language]);

    /**
     * Change language
     */
    const changeLanguage = useCallback((newLang) => {
        if (translations[newLang]) {
            setLanguage(newLang);
        }
    }, []);

    /**
     * Get current language code
     */
    const getCurrentLanguage = useCallback(() => language, [language]);

    /**
     * Get language name for display
     */
    const getLanguageName = useCallback((code = language) => {
        return languageNames[code] || code;
    }, [language]);

    /**
     * Get full language name (for AI)
     */
    const getLanguageFullName = useCallback((code = language) => {
        return languageFullNames[code] || 'English';
    }, [language]);

    /**
     * Get all available languages
     */
    const getAvailableLanguages = useCallback(() => {
        return Object.keys(translations).map(code => ({
            code,
            name: languageNames[code],
            fullName: languageFullNames[code],
        }));
    }, []);

    const value = {
        language,
        t,
        changeLanguage,
        getCurrentLanguage,
        getLanguageName,
        getLanguageFullName,
        getAvailableLanguages,
        translations,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

/**
 * useLanguage - Custom hook to use language context
 */
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext;
